// src/api/endpoints.js
import { nodeApiPath } from "../utils/paths";

// ========== INTERCEPTOR GLOBAL DE SESIÓN ==========
// Monkey-patch fetch para interceptar TODOS los 401 y disparar sesión expirada
const _originalFetch = window.fetch;
let _sessionExpiredFired = false; // Evitar disparar múltiples veces
export let isSessionExpired = false; // Flag global para que componentes sepan si la sesión expiró

window.fetch = async function (...args) {
    const response = await _originalFetch.apply(this, args);
    if (response.status === 401) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        if (url.includes('/api/') && !url.includes('/api/auth/login')) {
            // IMPORTANTE: no todo 401 es "sesión expirada". El authMiddleware
            // marca los 401 de token con code NO_TOKEN / INVALID_TOKEN. Los 401
            // de lógica de negocio (ej: endpoint que valida id_personal y
            // responde "No autenticado") NO tienen ese code — y si los tratamos
            // como sesión expirada, el modal salta en cada poll de 30/60s.
            // Solo disparamos sesión expirada cuando es realmente el token.
            let esTokenInvalido = false;
            try {
                const clone = response.clone();
                const body = await clone.json();
                esTokenInvalido = body?.code === 'NO_TOKEN' || body?.code === 'INVALID_TOKEN' || body?.code === 'SESSION_EXPIRED';
            } catch {
                // Si no se puede parsear el body, asumimos que es token (conservador)
                esTokenInvalido = true;
            }
            if (esTokenInvalido) {
                localStorage.removeItem("SIGIF_JWT_TOKEN");
                triggerSessionExpired();
            }
        }
    }
    return response;
};

// Helper: dispara sesión expirada cuando no hay token (llamado desde funciones que verifican token antes del fetch)
export function triggerSessionExpired() {
    isSessionExpired = true;
    if (!_sessionExpiredFired) {
        _sessionExpiredFired = true;
        window.dispatchEvent(new CustomEvent('sigif-session-expired'));
        setTimeout(() => { _sessionExpiredFired = false; }, 5000);
    }
}

// Resetear flag cuando se re-loguea
export function resetSessionExpired() {
    isSessionExpired = false;
    _sessionExpiredFired = false;
}

// ========== BIBLIOTECA ==========
export function bibliotecaTiposJWT() { return jwtFetch('biblioteca/tipos'); }
export function bibliotecaReferenciasJWT() { return jwtFetch('biblioteca/referencias'); }
export function bibliotecaStatsJWT() { return jwtFetch('biblioteca/stats'); }
export function bibliotecaListarJWT(filtros = {}) {
    const p = new URLSearchParams();
    if (filtros.busqueda) p.append('busqueda', filtros.busqueda);
    if (filtros.tipoId) p.append('tipoId', filtros.tipoId);
    if (filtros.referenciaId) p.append('referenciaId', filtros.referenciaId);
    if (filtros.anio) p.append('anio', filtros.anio);
    if (filtros.page) p.append('page', filtros.page);
    if (filtros.pageSize) p.append('pageSize', filtros.pageSize);
    if (filtros.verRestringidos) p.append('verRestringidos', filtros.verRestringidos);
    return jwtFetch(`biblioteca?${p}`);
}
export function bibliotecaObtenerJWT(id) { return jwtFetch(`biblioteca/${id}`); }
export function bibliotecaCrearJWT(data) { return jwtFetch('biblioteca', { method: 'POST', body: JSON.stringify(data) }); }
export function bibliotecaEditarJWT(id, data) { return jwtFetch(`biblioteca/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
export function bibliotecaEliminarJWT(id) { return jwtFetch(`biblioteca/${id}`, { method: 'DELETE' }); }
export function bibliotecaSiguienteNumeroJWT(tipoId, anio) { return jwtFetch(`biblioteca/siguiente-numero?tipoId=${tipoId}&anio=${anio}`); }

// ========== PERMISOS (ADMIN RBAC) ==========
export function permisosArbolJWT() { return jwtFetch('permisos/arbol'); }
export function permisosBuscarJWT(q) { return jwtFetch(`permisos/buscar?q=${encodeURIComponent(q)}`); }
export function permisosStatsJWT() { return jwtFetch('permisos/stats'); }
export function permisosModulosJWT() { return jwtFetch('permisos/modulos'); }
export function permisosFuncionesJWT(idModulo) {
    const params = idModulo !== undefined ? `?idModulo=${idModulo}` : '';
    return jwtFetch(`permisos/funciones${params}`);
}
export function permisosGruposJWT() { return jwtFetch('permisos/grupos'); }
export function permisosCrearGrupoJWT(nombre) { return jwtFetch('permisos/grupos', { method: 'POST', body: JSON.stringify({ nombre }) }); }
export function permisosEditarGrupoJWT(id, nombre) { return jwtFetch(`permisos/grupos/${id}`, { method: 'PUT', body: JSON.stringify({ nombre }) }); }
export function permisosEliminarGrupoJWT(id) { return jwtFetch(`permisos/grupos/${id}`, { method: 'DELETE' }); }
export function permisosCrearSubGrupoJWT(nombre, idGrupo) { return jwtFetch('permisos/subgrupos', { method: 'POST', body: JSON.stringify({ nombre, idGrupo }) }); }
export function permisosEditarSubGrupoJWT(id, nombre) { return jwtFetch(`permisos/subgrupos/${id}`, { method: 'PUT', body: JSON.stringify({ nombre }) }); }
export function permisosEliminarSubGrupoJWT(id) { return jwtFetch(`permisos/subgrupos/${id}`, { method: 'DELETE' }); }
export function permisosObtenerUsuarioJWT(id) { return jwtFetch(`permisos/usuarios/${id}`); }
export function permisosCrearUsuarioJWT(data) { return jwtFetch('permisos/usuarios', { method: 'POST', body: JSON.stringify(data) }); }
export function permisosEditarUsuarioJWT(id, data) { return jwtFetch(`permisos/usuarios/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
export function permisosCambiarPasswordJWT(id, password) { return jwtFetch(`permisos/usuarios/${id}/password`, { method: 'PUT', body: JSON.stringify({ password }) }); }
export function permisosUsuarioPermisosJWT(id) { return jwtFetch(`permisos/usuarios/${id}/permisos`); }
export function permisosGuardarPermisosJWT(id, idModulo, permisos) { return jwtFetch(`permisos/usuarios/${id}/permisos`, { method: 'POST', body: JSON.stringify({ idModulo, permisos }) }); }
export function permisosAsignarMasivoJWT(idSubGrupo, idModulo, permisos) { return jwtFetch('permisos/permisos/masivo', { method: 'POST', body: JSON.stringify({ idSubGrupo, idModulo, permisos }) }); }
export function permisosCopiarPermisosJWT(idOrigen, idDestino) { return jwtFetch('permisos/copiar-permisos', { method: 'POST', body: JSON.stringify({ idOrigen, idDestino }) }); }
export function permisosContarJWT() { return jwtFetch('permisos/contar-permisos'); }
export function permisosExportarJWT(id) { return jwtFetch(`permisos/usuarios/${id}/exportar-permisos`); }

// ========== MENSAJE BIENVENIDA (BANNER DASHBOARD) ==========
export function mensajeBienvenidaJWT() { return jwtFetch('admin/catalogos/sistema/mensaje'); }
// Multi-banner: lista de banners visibles para mostrar en el dashboard.
export function bannersVisiblesJWT() { return jwtFetch('admin/catalogos/sistema/banners'); }
// Lista de TODOS los banners (admin).
export function bannersListarAdminJWT() { return jwtFetch('admin/catalogos/sistema/banners/admin'); }
export function bannerCrearJWT(payload) {
    return jwtFetch('admin/catalogos/sistema/banners', { method: 'POST', body: JSON.stringify(payload) });
}
export function bannerActualizarJWT(id, payload) {
    return jwtFetch(`admin/catalogos/sistema/banners/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}
export function bannerToggleJWT(id, visible) {
    return jwtFetch(`admin/catalogos/sistema/banners/${id}/toggle`, { method: 'POST', body: JSON.stringify({ visible }) });
}
export function bannerEliminarJWT(id) {
    return jwtFetch(`admin/catalogos/sistema/banners/${id}`, { method: 'DELETE' });
}

// ========== CUSTODIA DE ARCHIVOS ==========
export function custodiaEstadosJWT() { return jwtFetch('custodia/estados'); }
export function custodiaMovTiposJWT() { return jwtFetch('custodia/movimientos-tipos'); }
export function custodiaStatsJWT() { return jwtFetch('custodia/stats'); }
export function custodiaListarJWT(filtros = {}) {
    const p = new URLSearchParams();
    if (filtros.busqueda) p.append('busqueda', filtros.busqueda);
    if (filtros.estadoId) p.append('estadoId', filtros.estadoId);
    if (filtros.page) p.append('page', filtros.page);
    if (filtros.pageSize) p.append('pageSize', filtros.pageSize);
    return jwtFetch(`custodia?${p}`);
}
export function custodiaObtenerJWT(id) { return jwtFetch(`custodia/${id}`); }
export function custodiaCrearJWT(data) { return jwtFetch('custodia', { method: 'POST', body: JSON.stringify(data) }); }
export function custodiaEditarJWT(id, data) { return jwtFetch(`custodia/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
export function custodiaAnularJWT(id) { return jwtFetch(`custodia/${id}`, { method: 'DELETE' }); }
export function custodiaMovimientosJWT(id) { return jwtFetch(`custodia/${id}/movimientos`); }
export function custodiaAgregarMovJWT(id, data) { return jwtFetch(`custodia/${id}/movimientos`, { method: 'POST', body: JSON.stringify(data) }); }
export function custodiaDocumentosJWT(id) { return jwtFetch(`custodia/${id}/documentos`); }
export function custodiaAgregarDocJWT(id, data) { return jwtFetch(`custodia/${id}/documentos`, { method: 'POST', body: JSON.stringify(data) }); }
export function custodiaQuitarDocJWT(idRelacion) { return jwtFetch(`custodia/documento/${idRelacion}`, { method: 'DELETE' }); }

// ========== ENDPOINTS JWT (Node.js) ==========

/**
 * loginJWT - Login con JWT (nuevo backend Node.js)
 * Retorna: { ok: true, token, usuario } | { ok: false, mensaje }
 */
export async function loginJWT({ usuario, password }) {
    try {
        const res = await fetch(nodeApiPath("auth/login"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
            body: JSON.stringify({ usuario, password }),
        });

        // La respuesta puede no ser JSON (ej. una página de error del proxy),
        // así que el parseo no debe tumbar el manejo del status.
        let data = null;
        try { data = await res.json(); } catch { /* respuesta no-JSON */ }

        if (res.ok && data?.ok) {
            // Guardar token en localStorage
            localStorage.setItem("SIGIF_JWT_TOKEN", data.token);
            return { ok: true, usuario: data.usuario, token: data.token };
        }

        // Distinguir el motivo real. Antes todo caía en "Usuario o contraseña
        // incorrectos" o en "Error al conectar", y eso confunde: tras un reinicio
        // del servidor el frontend estático levanta al instante pero el backend
        // tarda ~2 minutos en tener base, así que el login se ve perfecto y
        // devuelve 500. El usuario concluye que el sistema está roto en vez de
        // esperar. Ver DEPLOY.md §6.2.
        if (res.status === 429) {
            return {
                ok: false,
                mensaje: data?.mensaje || "Demasiados intentos seguidos. Esperá unos segundos y volvé a probar.",
            };
        }
        if (res.status >= 500) {
            return {
                ok: false,
                mensaje: "El servidor no está respondiendo. Si acaba de reiniciarse, aguardá un minuto y volvé a intentar.",
            };
        }

        return { ok: false, mensaje: data?.mensaje || "Usuario o contraseña incorrectos" };
    } catch (error) {
        // Ni siquiera hubo respuesta: no se llegó al servidor.
        return { ok: false, mensaje: "No se pudo contactar al servidor. Verificá tu conexión a la red." };
    }
}

/**
 * userInfoJWT - Obtener info del usuario con JWT
 */
export async function userInfoJWT() {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath("auth/user-info"), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            // Token inválido o expirado
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        // NO borrar el token acá. Un error transitorio (500/429, corte de red,
        // reinicio del backend, timeout) NO significa que la sesión venció —
        // borrar el token ante cualquier fallo mataba la sesión por un blip.
        // El token solo se borra ante un 401 real de token (bloque de arriba).
        throw error;
    }
}

/**
 * permisosUsuarioJWT - Obtener permisos del usuario con JWT
 */
export async function permisosUsuarioJWT() {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath("auth/permisos"), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        // NO borrar el token acá. Un error transitorio (500/429, corte de red,
        // reinicio del backend, timeout) NO significa que la sesión venció —
        // borrar el token ante cualquier fallo mataba la sesión por un blip.
        // El token solo se borra ante un 401 real de token (bloque de arriba).
        throw error;
    }
}

/**
 * logoutJWT - Cerrar sesión JWT
 */
export async function logoutJWT() {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    localStorage.removeItem("SIGIF_JWT_TOKEN");

    if (token) {
        try {
            await fetch(nodeApiPath("auth/logout"), {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
        } catch {
            // Ignorar errores en logout
        }
    }

    return { ok: true };
}

/**
 * refreshJWT - Refrescar token JWT (genera un nuevo token con 1h de validez)
 * Retorna: { ok: true, token } | { ok: false, mensaje }
 */
export async function refreshJWT() {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) {
        return { ok: false, mensaje: "No hay token para refrescar" };
    }

    try {
        const res = await fetch(nodeApiPath("auth/refresh"), {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.ok && data.ok && data.token) {
            // Actualizar token en localStorage
            localStorage.setItem("SIGIF_JWT_TOKEN", data.token);
            console.log('[REFRESH] Token actualizado exitosamente');
            return { ok: true, token: data.token };
        }

        // Token inválido o expirado
        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
        }

        return { ok: false, mensaje: data.mensaje || "Error al refrescar token" };
    } catch (error) {
        console.error("[REFRESH] Error:", error);
        return { ok: false, mensaje: "Error de conexión al refrescar token" };
    }
}

// ========== DASHBOARD ==========

/**
 * dashboardStatsJWT - Obtener estadísticas del dashboard con JWT
 * Retorna: { ok: true, stats } | { ok: false, mensaje }
 */
export async function dashboardStatsJWT() {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath("dashboard/stats"), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[DASHBOARD] Error:", error);
        throw error;
    }
}

// ========== DASHBOARD EJECUTIVO (Fiscal y delegados) ==========
export const dashboardEjecutivoAccesoJWT = () => jwtFetch('dashboard-ejecutivo/acceso');
// 5 endpoints, uno por tab (lazy load)
export const dashEjecResumenJWT = (desde, hasta) => {
    const p = new URLSearchParams();
    if (desde) p.append('desde', desde);
    if (hasta) p.append('hasta', hasta);
    const qs = p.toString();
    return jwtFetch(`dashboard-ejecutivo/resumen${qs ? '?' + qs : ''}`);
};
export const dashEjecFinancieroJWT = () => jwtFetch('dashboard-ejecutivo/financiero');
// Tab Comunicado — estadística certificada. Sin desde/hasta = histórico completo.
export const dashEjecComunicadoJWT = (desde, hasta) => {
    const p = new URLSearchParams();
    if (desde) p.append('desde', desde);
    if (hasta) p.append('hasta', hasta);
    const qs = p.toString();
    return jwtFetch(`dashboard-ejecutivo/comunicado${qs ? '?' + qs : ''}`);
};
export const dashEjecOperativoJWT = () => jwtFetch('dashboard-ejecutivo/operativo');
export const dashEjecComparativasJWT = (desde, hasta) => {
    const p = new URLSearchParams();
    if (desde) p.append('desde', desde);
    if (hasta) p.append('hasta', hasta);
    const qs = p.toString();
    return jwtFetch(`dashboard-ejecutivo/comparativas${qs ? '?' + qs : ''}`);
};
export const dashEjecPersonalJWT = () => jwtFetch('dashboard-ejecutivo/personal');
export const dashEjecProductividadJWT = (desde, hasta) => {
    const p = new URLSearchParams();
    if (desde) p.append('desde', desde);
    if (hasta) p.append('hasta', hasta);
    const qs = p.toString();
    return jwtFetch(`dashboard-ejecutivo/productividad${qs ? '?' + qs : ''}`);
};
// Tab Asignación — mapa de carga de abogados.
// idArea opcional (null = global para fiscal/delegado). El backend lo fuerza
// a la del director si el usuario tiene ese rol.
export const dashEjecAsignacionJWT = (idArea, desde, hasta) => {
    const params = [];
    if (idArea) params.push(`id_area=${idArea}`);
    if (desde) params.push(`desde=${encodeURIComponent(desde)}`);
    if (hasta) params.push(`hasta=${encodeURIComponent(hasta)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return jwtFetch(`dashboard-ejecutivo/asignacion${qs}`);
};

// Info + invalidar cache del dashboard
export const dashEjecInfoCacheJWT = () => jwtFetch('dashboard-ejecutivo/info-cache');
export const dashEjecInvalidarCacheJWT = () =>
    jwtFetch('dashboard-ejecutivo/invalidar-cache', { method: 'POST' });

// Mapa calor de San Juan (por departamento)
export const dashEjecMapaCalorJWT = () => jwtFetch('dashboard-ejecutivo/mapa-calor');
export const dashEjecMapaCalorDetalleJWT = (depto) =>
    jwtFetch(`dashboard-ejecutivo/mapa-calor/${encodeURIComponent(depto)}`);

// Detalle por abogado + búsqueda
export const dashEjecAbogadoDetalleJWT = (idPersonal, desde, hasta) => {
    const p = new URLSearchParams();
    if (desde) p.append('desde', desde);
    if (hasta) p.append('hasta', hasta);
    const qs = p.toString();
    return jwtFetch(`dashboard-ejecutivo/abogados/${idPersonal}${qs ? '?' + qs : ''}`);
};
export const dashEjecBuscarAbogadosJWT = (q) =>
    jwtFetch(`dashboard-ejecutivo/abogados/buscar?q=${encodeURIComponent(q || '')}`);

// Alertas configurables
export const dashEjecAlertasJWT = () => jwtFetch('dashboard-ejecutivo/alertas');
export const dashEjecActualizarAlertaJWT = (tipo, umbral, activa) =>
    jwtFetch(`dashboard-ejecutivo/alertas/${tipo}`, {
        method: 'PUT',
        body: JSON.stringify({ umbral, activa }),
    });
// Delegados
export const dashboardEjecutivoDelegadosJWT = () => jwtFetch('dashboard-ejecutivo/delegados');
export const dashboardEjecutivoAgregarDelegadoJWT = (idPersonal) =>
    jwtFetch('dashboard-ejecutivo/delegados', { method: 'POST', body: JSON.stringify({ idPersonal }) });
export const dashboardEjecutivoQuitarDelegadoJWT = (idPersonal) =>
    jwtFetch(`dashboard-ejecutivo/delegados/${idPersonal}`, { method: 'DELETE' });

// ========== CATÁLOGOS / COMBOS ==========

/**
 * getCombosJWT - Obtener catálogos/combos con JWT
 * @param {string} tipo - Tipo de catálogo (reparticiones, modulos, caracteres, etc.)
 * @param {string} q - Query de búsqueda opcional
 * @param {number} top - Límite de resultados (default: 50)
 * Retorna: { ok: true, items: [...] } | { ok: false, error }
 */
export async function getCombosJWT(tipo, q = '', top = 50) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const params = new URLSearchParams({ tipo });
        if (q) params.append('q', q);
        if (top !== 50) params.append('top', top.toString());

        const res = await fetch(nodeApiPath(`catalogos/combos?${params.toString()}`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[CATALOGOS] Error:", error);
        throw error;
    }
}

// ========== NOTIFICACIONES ==========

/**
 * notificacionesListarJWT - Listar notificaciones del usuario con JWT
 * Retorna: { ok: true, notificaciones: [...], noLeidas: number } | { ok: false, mensaje }
 */
export async function notificacionesListarJWT() {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath("notificaciones"), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[NOTIFICACIONES] Error:", error);
        throw error;
    }
}

/**
 * notificacionMarcarLeidaJWT - Marcar una notificación como leída
 * @param {number} id - ID de la notificación
 */
export async function notificacionMarcarLeidaJWT(id) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`notificaciones/${id}/marcar-leida`), {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[NOTIFICACIONES] Error:", error);
        throw error;
    }
}

/**
 * notificacionesMarcarTodasLeidasJWT - Marcar todas las notificaciones como leídas
 */
export async function notificacionesMarcarTodasLeidasJWT() {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath("notificaciones/marcar-todas-leidas"), {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[NOTIFICACIONES] Error:", error);
        throw error;
    }
}

/**
 * notificacionEliminarJWT - Eliminar una notificación
 * @param {number} id - ID de la notificación
 */
export async function notificacionEliminarJWT(id) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`notificaciones/${id}`), {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[NOTIFICACIONES] Error:", error);
        throw error;
    }
}

// ========== EXPEDIENTES ==========

/**
 * expedientesHomeJWT - Obtener listado de expedientes (home)
 * Retorna: { ok: true, expedientes: [...], total: number } | { ok: false, mensaje }
 */
export async function expedientesHomeJWT() {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath("expedientes/home"), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[EXPEDIENTES] Error:", error);
        throw error;
    }
}

/**
 * expedienteDetalleJWT - Obtener detalle completo de un expediente
 * @param {number} id - ID del expediente
 * Retorna: { ok: true, expediente: {...} } | { ok: false, mensaje }
 */
export async function expedienteDetalleJWT(id) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${id}`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[EXPEDIENTES] Error:", error);
        throw error;
    }
}

export async function expedienteCertificadosJWT(id) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${id}/certificados`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[EXPEDIENTES CERTIFICADOS] Error:", error);
        throw error;
    }
}

/**
 * certificadoPdfJWT - Obtener URL del PDF de un certificado
 * Retorna blob URL para abrir en nueva pestaña, o null si no hay PDF
 */
export async function certificadoPdfJWT(idExpediente, idCertificado) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/certificados/${idCertificado}/pdf`), {
        method: "GET",
        headers: { "Authorization": `Bearer ${token}` },
    });

    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Error al obtener PDF");

    const blob = await res.blob();
    return URL.createObjectURL(blob);
}

/**
 * movimientoPdfJWT - Obtener PDF vinculado a un movimiento (desde PDFBase64)
 * Retorna blob URL para abrir en nueva pestaña, o null si no hay PDF
 */
export async function movimientoPdfJWT(idExpediente, idMovimiento) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/movimientos/${idMovimiento}/pdf`), {
        method: "GET",
        headers: { "Authorization": `Bearer ${token}` },
    });

    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Error al obtener PDF");

    const blob = await res.blob();
    return URL.createObjectURL(blob);
}

/**
 * expedienteInmueblesJWT - Obtener inmuebles de un expediente
 */
export async function expedienteInmueblesJWT(id) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${id}/inmuebles`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[EXPEDIENTES INMUEBLES] Error:", error);
        throw error;
    }
}

/**
 * expedienteRadicacionJWT - Obtener radicación de un expediente
 */
export async function expedienteRadicacionJWT(id) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${id}/radicacion`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[EXPEDIENTES RADICACION] Error:", error);
        throw error;
    }
}

// ========== EDITAR EXPEDIENTE ==========

export function editarExpedienteJWT(id, data) {
    // Convert snake_case from frontend form to camelCase expected by backend
    const payload = {
        caratula: data.caratula,
        idTipoProceso: data.id_tipo_proceso,
        idObjetoReclamo: data.id_objeto_reclamo,
        idEstado: data.id_estado,
        idModulo: data.id_modulo,
        fechaInicio: data.fecha_inicio,
        fechaAsignacion: data.fecha_asignacion,
        autos1Instancia: data.autos_1_instancia,
        // Administrativo fields
        expedienteAdm: data.expediente_adm,
        iniciador: data.iniciador,
        extracto: data.extracto,
        idReparticion: data.id_reparticion,
    };
    return jwtFetch(`expedientes/${id}`, {
        method: 'PUT', body: JSON.stringify(payload),
    });
}

// ========== AUTOS / INSTANCIAS ==========

export function expedienteAutosJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/autos`);
}

export function guardarAutoJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/autos`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

// ========== CARTA PODER ==========

export function expedienteCartaPoderJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/carta-poder`);
}

// ========== AREAS CONJUNTAS ==========

export function expedienteAreasConjuntasJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/areas-conjuntas`);
}

export function agregarAreaConjuntaJWT(idExpediente, idModulo) {
    return jwtFetch(`expedientes/${idExpediente}/areas-conjuntas`, {
        method: 'POST', body: JSON.stringify({ id_modulo: idModulo }),
    });
}

export function eliminarAreaConjuntaJWT(idExpediente, id) {
    return jwtFetch(`expedientes/${idExpediente}/areas-conjuntas/${id}`, { method: 'DELETE' });
}

// ========== RIESGO Y RELEVANCIA ==========

export function expedienteRiesgoRelevanciaJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/riesgo-relevancia`);
}

export function guardarRiesgoRelevanciaJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/riesgo-relevancia`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

// ========== EMBARGOS ==========

export function expedienteEmbargosJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/embargos`);
}

export function embargoByMovimientoJWT(idExpediente, idMovimiento) {
    return jwtFetch(`expedientes/${idExpediente}/movimientos/${idMovimiento}/embargo`);
}

// ========== NOTAS PERSONALES ==========

export function notasListarJWT(idExpediente) {
    return jwtFetch(`notas/${idExpediente}`);
}

export function notasCrearJWT(idExpediente, data) {
    return jwtFetch(`notas/${idExpediente}`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function notasEditarJWT(idExpediente, id, data) {
    return jwtFetch(`notas/${idExpediente}/${id}`, {
        method: 'PUT', body: JSON.stringify(data),
    });
}

export function notasToggleFijarJWT(idExpediente, id) {
    return jwtFetch(`notas/${idExpediente}/${id}/fijar`, { method: 'POST' });
}

export function notasEliminarJWT(idExpediente, id) {
    return jwtFetch(`notas/${idExpediente}/${id}`, { method: 'DELETE' });
}

// ========== SOLICITUDES DE PAGO (vista desde expediente) ==========

export function hasSolicitudesPagoJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/has-solicitudes-pago`);
}

export function solicitudesPagoExpedienteJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/solicitudes-pago`);
}

// ========== RESOLUCIONES ==========

export function resolucionesExpedienteJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/resoluciones`);
}

export function crearResolucionJWT(idExpediente, formData) {
    return jwtFetch(`expedientes/${idExpediente}/resoluciones`, {
        method: 'POST',
        body: formData,
    });
}

export async function descargarResolucionJWT(idExpediente, idResolucion) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }
    const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/resoluciones/${idResolucion}/download`), {
        headers: { "Authorization": `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Error al descargar");
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="(.+?)"/);
    const filename = match ? match[1] : 'documento.pdf';
    return { blob, filename };
}

export function eliminarResolucionJWT(idExpediente, idResolucion) {
    return jwtFetch(`expedientes/${idExpediente}/resoluciones/${idResolucion}`, { method: 'DELETE' });
}

// ========== CERTIFICADOS CRUD ==========

export function crearCertificadoJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/certificados`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function editarCertificadoJWT(idExpediente, id, data) {
    return jwtFetch(`expedientes/${idExpediente}/certificados/${id}`, {
        method: 'PUT', body: JSON.stringify(data),
    });
}

export function eliminarCertificadoJWT(idExpediente, id) {
    return jwtFetch(`expedientes/${idExpediente}/certificados/${id}`, { method: 'DELETE' });
}

// ========== RADICACION SAVE ==========

export function guardarRadicacionJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/radicacion`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function eliminarRadicacionHistorialJWT(idExpediente, id) {
    return jwtFetch(`expedientes/${idExpediente}/radicacion/${id}`, { method: 'DELETE' });
}

// ========== CONCURSOS ==========

export function expedienteConcursoJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/concurso`);
}

export function guardarConcursoJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/concurso`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function expedienteAcreedoresJWT(idExpediente, grilla = 'C') {
    return jwtFetch(`expedientes/${idExpediente}/acreedores?grilla=${grilla}`);
}

export function crearAcreedorJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/acreedores`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function editarAcreedorJWT(idExpediente, id, data) {
    return jwtFetch(`expedientes/${idExpediente}/acreedores/${id}`, {
        method: 'PUT', body: JSON.stringify(data),
    });
}

export function eliminarAcreedorJWT(idExpediente, id) {
    return jwtFetch(`expedientes/${idExpediente}/acreedores/${id}`, { method: 'DELETE' });
}

// ========== BÚSQUEDA JUDICIALES PARA MEDIACIÓN ==========

export function buscarJudicialesParaMediacionJWT(q) {
    return jwtFetch(`expedientes/buscar-judiciales?q=${encodeURIComponent(q)}`);
}

export function buscarAdministrativosJWT(q) {
    return jwtFetch(`expedientes/buscar-administrativos?q=${encodeURIComponent(q)}`);
}

// ========== MEDIACIONES ==========

export function expedienteMediacionesJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/mediaciones`);
}

export function guardarMediacionRadicacionJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/mediaciones/radicacion`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function guardarMediacionAcuerdoJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/mediaciones/acuerdo`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function guardarMediacionNotificacionJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/mediaciones/notificacion`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function crearAudienciaJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/mediaciones/audiencias`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function editarAudienciaJWT(idExpediente, id, data) {
    return jwtFetch(`expedientes/${idExpediente}/mediaciones/audiencias/${id}`, {
        method: 'PUT', body: JSON.stringify(data),
    });
}

export function eliminarAudienciaJWT(idExpediente, id) {
    return jwtFetch(`expedientes/${idExpediente}/mediaciones/audiencias/${id}`, { method: 'DELETE' });
}

// ========== INMUEBLES CRUD ==========

export function crearInmuebleJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/inmuebles`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function editarInmuebleJWT(idExpediente, id, data) {
    return jwtFetch(`expedientes/${idExpediente}/inmuebles/${id}`, {
        method: 'PUT', body: JSON.stringify(data),
    });
}

export function eliminarInmuebleJWT(idExpediente, id) {
    return jwtFetch(`expedientes/${idExpediente}/inmuebles/${id}`, { method: 'DELETE' });
}

// ========== TITULARES ==========

export function expedienteTitularesJWT(idExpediente, idInmueble) {
    return jwtFetch(`expedientes/${idExpediente}/inmuebles/${idInmueble}/titulares`);
}

export function crearTitularJWT(idExpediente, idInmueble, data) {
    return jwtFetch(`expedientes/${idExpediente}/inmuebles/${idInmueble}/titulares`, {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function editarTitularJWT(idExpediente, idInmueble, idContraparte, data) {
    return jwtFetch(`expedientes/${idExpediente}/inmuebles/${idInmueble}/titulares/${idContraparte}`, {
        method: 'PUT', body: JSON.stringify(data),
    });
}

export function eliminarTitularJWT(idExpediente, idInmueble, idContraparte) {
    return jwtFetch(`expedientes/${idExpediente}/inmuebles/${idInmueble}/titulares/${idContraparte}`, { method: 'DELETE' });
}

export function siguienteDniFicticioJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/inmuebles/siguiente-dni-ficticio`);
}

export function buscarContrapartePorDniJWT(idExpediente, dni) {
    return jwtFetch(`expedientes/${idExpediente}/inmuebles/buscar-contraparte/${dni}`);
}

// ========== VISIBILIDAD ==========

export function expedienteVisibilidadJWT(tabla, idsModulos = []) {
    const params = new URLSearchParams({ tabla });
    if (idsModulos.length) params.append('id_modulo', idsModulos.join(','));
    return jwtFetch(`expedientes/visibilidad?${params}`);
}

/**
 * expedientesBusquedaUnificadaJWT - Búsqueda avanzada de expedientes
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} params.q - Texto de búsqueda
 * @param {number} params.tipo - Tipo de expediente (1=Judicial, 2=Administrativo)
 * @param {number} params.modulo - ID del módulo
 * @param {string} params.fechaDesde - Fecha desde (YYYY-MM-DD)
 * @param {string} params.fechaHasta - Fecha hasta (YYYY-MM-DD)
 * @param {number} params.limit - Límite de resultados
 * @param {number} params.offset - Offset para paginación
 */
/**
 * Trae SOLO los ids de todo lo que coincide, sin el tope de la pagina. Lo usa
 * "seleccionar los N que coinciden" de la grilla. El backend topea en 25.000.
 */
export async function expedientesIdsJWT(params = {}) {
    return expedientesBusquedaUnificadaJWT({ ...params, soloIds: true });
}

export async function expedientesBusquedaUnificadaJWT(params = {}) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const queryParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
            if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
                queryParams.append(key, params[key].toString());
            }
        });

        const res = await fetch(nodeApiPath(`expedientes/busqueda-unificada?${queryParams.toString()}`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[EXPEDIENTES] Error:", error);
        throw error;
    }
}

/**
 * expedienteEliminarJWT - Eliminar expediente (soft delete)
 */
export async function expedienteEliminarJWT(id) {
    return jwtFetch(`expedientes/${id}`, { method: 'DELETE' });
}

// ========== PARTES ==========

/**
 * expedientePartesJWT - Obtener partes de un expediente
 * @param {number} idExpediente - ID del expediente
 * Retorna: { ok: true, partes: { provincia: [...], contraparte: [...], auxiliares: [...] } }
 */
export async function expedientePartesJWT(idExpediente) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/partes`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[PARTES] Error:", error);
        throw error;
    }
}

/**
 * crearParteJWT - Crear una nueva parte en un expediente
 * @param {number} idExpediente - ID del expediente
 * @param {Object} parte - Datos de la parte
 */
export async function crearParteJWT(idExpediente, parte) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/partes`), {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(parte),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[PARTES] Error:", error);
        throw error;
    }
}

/**
 * editarParteJWT - Editar una parte existente
 * @param {number} idExpediente - ID del expediente
 * @param {number} idParte - ID de la parte
 * @param {Object} parte - Datos actualizados de la parte
 */
export async function editarParteJWT(idExpediente, idParte, parte) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/partes/${idParte}`), {
            method: "PUT",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(parte),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[PARTES] Error:", error);
        throw error;
    }
}

/**
 * eliminarParteJWT - Eliminar una parte (soft delete)
 * @param {number} idExpediente - ID del expediente
 * @param {number} idParte - ID de la parte
 */
export async function eliminarParteJWT(idExpediente, idParte) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/partes/${idParte}`), {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[PARTES] Error:", error);
        throw error;
    }
}

/**
 * getReparticionesParteJWT - Reparticiones elegibles para la grilla Provincia de Partes.
 * Filtra por el módulo del expediente y por el carácter elegido, como el combo del sistema
 * viejo. NO usar el combo genérico 'reparticiones' para esto: ese no filtra y lo usan
 * otras pantallas.
 * @param {number} idExpediente
 * @param {number|string|null} idCaracter - sin carácter, el backend no filtra por carácter
 * @param {string} q - texto a buscar
 */
export function getReparticionesParteJWT(idExpediente, idCaracter, q = '', top = 50) {
    const params = new URLSearchParams();
    if (idCaracter) params.append('caracter', String(idCaracter));
    if (q) params.append('q', q);
    if (top !== 50) params.append('top', String(top));
    return jwtFetch(`expedientes/${idExpediente}/partes/reparticiones?${params.toString()}`);
}

/**
 * crearContraparteJWT - Crear una nueva contraparte (cuando no existe en la BD)
 * @param {number} idExpediente - ID del expediente (contexto)
 * @param {Object} contraparte - { id, razonSocial, nombre, direccion, cp, matricula, idTipoDocumento }
 */
export async function crearContraparteJWT(idExpediente, contraparte) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/partes/contraparte`), {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(contraparte),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[PARTES] Error al crear contraparte:", error);
        throw error;
    }
}

// ========== MOVIMIENTOS ==========

/**
 * expedienteMovimientosJWT - Obtener movimientos de un expediente
 * @param {number} idExpediente - ID del expediente
 * Retorna: { ok: true, movimientos: [...] }
 */
export async function expedienteMovimientosJWT(idExpediente) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/movimientos`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[MOVIMIENTOS] Error:", error);
        throw error;
    }
}

/**
 * crearMovimientoJWT - Crear un nuevo movimiento en un expediente
 * @param {number} idExpediente - ID del expediente
 * @param {Object} movimiento - Datos del movimiento
 */
export async function crearMovimientoJWT(idExpediente, movimiento) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/movimientos`), {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(movimiento),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[MOVIMIENTOS] Error:", error);
        throw error;
    }
}

/**
 * editarMovimientoJWT - Editar un movimiento existente
 * @param {number} idExpediente - ID del expediente
 * @param {number} idMovimiento - ID del movimiento
 * @param {Object} movimiento - Datos actualizados del movimiento
 */
export async function editarMovimientoJWT(idExpediente, idMovimiento, movimiento) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/movimientos/${idMovimiento}`), {
            method: "PUT",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(movimiento),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[MOVIMIENTOS] Error:", error);
        throw error;
    }
}

/**
 * eliminarMovimientoJWT - Eliminar un movimiento
 * @param {number} idExpediente - ID del expediente
 * @param {number} idMovimiento - ID del movimiento
 */
export async function eliminarMovimientoJWT(idExpediente, idMovimiento) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/movimientos/${idMovimiento}`), {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[MOVIMIENTOS] Error:", error);
        throw error;
    }
}

// ========== VINCULADOS ==========

/**
 * expedienteVinculadosJWT - Obtener vinculados (documentos adjuntos) de un expediente
 * @param {number} idExpediente - ID del expediente
 * Retorna: { ok: true, vinculados: [...], total: number }
 */
export async function expedienteVinculadosJWT(idExpediente) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/vinculados`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[VINCULADOS] Error:", error);
        throw error;
    }
}

/**
 * crearVinculadoJWT - Subir un archivo vinculado a un expediente
 * @param {number} idExpediente - ID del expediente
 * @param {File} archivo - Archivo a subir
 * @param {Object} datos - Datos del vinculado (idTipoArchivo, descripcion, generaMov)
 */
export async function crearVinculadoJWT(idExpediente, archivo, datos) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const idTipoArchivo = datos.idTipoArchivo || datos.id_tipo_archivo;
        const gm = datos.generaMov || datos.genera_mov;
        let fetchOpts;

        if (archivo) {
            // Con archivo → multipart FormData (campos de texto ANTES del archivo)
            const formData = new FormData();
            formData.append('idTipoArchivo', idTipoArchivo);
            formData.append('descripcion', datos.descripcion);
            if (gm) formData.append('generaMov', gm);
            if (datos.url_vinculado) formData.append('urlVinculado', datos.url_vinculado);
            formData.append('file', archivo);

            fetchOpts = {
                method: "POST",
                headers: { "Authorization": `Bearer ${token}` },
                body: formData,
            };
        } else {
            // Sin archivo → JSON body (ej: tipo Expediente)
            fetchOpts = {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    idTipoArchivo: Number(idTipoArchivo),
                    descripcion: datos.descripcion,
                    urlVinculado: datos.url_vinculado || '',
                    generaMov: gm === '1' || gm === 'true',
                }),
            };
        }

        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/vinculados`), fetchOpts);
        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[VINCULADOS] Error:", error);
        throw error;
    }
}

/**
 * downloadVinculadoJWT - Descargar un archivo vinculado
 * @param {number} idExpediente - ID del expediente
 * @param {number} idVinculado - ID del vinculado
 * @param {string} nombreArchivo - Nombre del archivo (opcional, para el nombre de descarga)
 */
export async function downloadVinculadoJWT(idExpediente, idVinculado, nombreArchivo = '') {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/vinculados/${idVinculado}/download`), {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        if (!res.ok) {
            const data = await res.json();
            throw new Error(data.mensaje || 'Error al descargar archivo');
        }

        // Obtener blob del archivo
        const blob = await res.blob();

        // Crear URL temporal
        const url = window.URL.createObjectURL(blob);

        // Crear link temporal y hacer click
        const a = document.createElement('a');
        a.href = url;
        a.download = nombreArchivo || `archivo_${idVinculado}`;
        document.body.appendChild(a);
        a.click();

        // Limpiar
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return { ok: true };
    } catch (error) {
        console.error("[VINCULADOS] Error:", error);
        throw error;
    }
}

/**
 * getVinculadoJWT - Obtener datos de un vinculado individual
 */
export async function getVinculadoJWT(idExpediente, idVinculado) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }
    const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/vinculados/${idVinculado}`), {
        headers: { "Authorization": `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return await res.json();
}

/**
 * eliminarVinculadoJWT - Eliminar un vinculado
 * @param {number} idExpediente - ID del expediente
 * @param {number} idVinculado - ID del vinculado
 */
export async function eliminarVinculadoJWT(idExpediente, idVinculado) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`expedientes/${idExpediente}/vinculados/${idVinculado}`), {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[VINCULADOS] Error:", error);
        throw error;
    }
}

// ========== LISTAS DE TRABAJO ==========

/**
 * listasTrabajoObtenerJWT - Obtener listas de trabajo del usuario
 * @param {number} tipoExpediente - Tipo de expediente (opcional)
 */
export async function listasTrabajoObtenerJWT(tipoExpediente = null) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const params = new URLSearchParams();
        if (tipoExpediente) params.append('tipoExpediente', tipoExpediente.toString());

        const url = params.toString()
            ? nodeApiPath(`listas-trabajo?${params.toString()}`)
            : nodeApiPath('listas-trabajo');

        const res = await fetch(url, {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[LISTAS TRABAJO] Error:", error);
        throw error;
    }
}

/**
 * listasTrabajoObtenerUnaJWT - Obtener una lista con sus expedientes
 * @param {number} idLista - ID de la lista
 */
export async function listasTrabajoObtenerUnaJWT(idLista, page, pageSize) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const params = page && pageSize ? `?page=${page}&pageSize=${pageSize}` : '';
        const res = await fetch(nodeApiPath(`listas-trabajo/${idLista}${params}`), {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[LISTAS TRABAJO] Error:", error);
        throw error;
    }
}

/**
 * listasTrabajoExpedientesCompletosJWT - Obtener expedientes con datos completos para reportes
 */
export function listasTrabajoExpedientesCompletosJWT(idLista) {
    return jwtFetch(`listas-trabajo/${idLista}/expedientes-completos`);
}

/**
 * listasTrabajoCrearJWT - Crear una nueva lista de trabajo
 */
export async function listasTrabajoCrearJWT(datos) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath('listas-trabajo'), {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(datos),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[LISTAS TRABAJO] Error:", error);
        throw error;
    }
}

/**
 * listasTrabajoActualizarJWT - Actualizar una lista de trabajo
 */
export async function listasTrabajoActualizarJWT(idLista, datos) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`listas-trabajo/${idLista}`), {
            method: "PUT",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(datos),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[LISTAS TRABAJO] Error:", error);
        throw error;
    }
}

/**
 * listasTrabajoEliminarJWT - Eliminar una lista de trabajo
 */
export async function listasTrabajoEliminarJWT(idLista) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`listas-trabajo/${idLista}`), {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[LISTAS TRABAJO] Error:", error);
        throw error;
    }
}

/**
 * listasTrabajoAgregarExpedientesJWT - Agregar expedientes a una lista
 */
export async function listasTrabajoAgregarExpedientesJWT(idLista, expedientesIds) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`listas-trabajo/${idLista}/expedientes`), {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({ expedientesIds }),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[LISTAS TRABAJO] Error:", error);
        throw error;
    }
}

/**
 * listasTrabajoEliminarExpedientesJWT - Eliminar expedientes de una lista
 */
export async function listasTrabajoEliminarExpedientesJWT(idLista, expedientesIds) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`listas-trabajo/${idLista}/expedientes`), {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({ expedientesIds }),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[LISTAS TRABAJO] Error:", error);
        throw error;
    }
}

/**
 * listasTrabajoMarcarVistoJWT - Marcar expediente como visto en una lista
 */
export async function listasTrabajoMarcarVistoJWT(idLista, idExpediente) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`listas-trabajo/${idLista}/expedientes/${idExpediente}/visto`), {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[LISTAS TRABAJO] Error:", error);
        throw error;
    }
}

/** Toggle favorita de lista de trabajo */
export function listasTrabajoToggleFavoritaJWT(id, favorita) {
    return jwtFetch('listas-trabajo/favorita', {
        method: 'POST',
        body: JSON.stringify({ id, favorita }),
    });
}

/**
 * listasTrabajoActualizarNotasJWT - Actualizar notas de un expediente en una lista
 */
export async function listasTrabajoActualizarNotasJWT(idLista, idExpediente, notas) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    try {
        const res = await fetch(nodeApiPath(`listas-trabajo/${idLista}/expedientes/${idExpediente}/notas`), {
            method: "PUT",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({ notas }),
        });

        const data = await res.json();

        if (res.status === 401) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            throw new Error("Token expirado");
        }

        return data;
    } catch (error) {
        console.error("[LISTAS TRABAJO] Error:", error);
        throw error;
    }
}

/** Reordenar expedientes en una lista */
export function listasTrabajoReordenarJWT(idLista, orden) {
    return jwtFetch(`listas-trabajo/${idLista}/reordenar`, {
        method: 'PUT',
        body: JSON.stringify({ orden }),
    });
}

// ========== COMPARTIR LISTAS DE TRABAJO ==========

/** Obtener listas compartidas conmigo */
export function listasTrabajoCompartidasJWT(tipoExpediente) {
    const params = tipoExpediente ? `?tipoExpediente=${tipoExpediente}` : '';
    return jwtFetch(`listas-trabajo/compartidas${params}`);
}

/** Obtener personas con las que se compartió una lista */
export function listasTrabajoGetCompartidosJWT(idLista) {
    return jwtFetch(`listas-trabajo/compartidos/${idLista}`);
}

/** Compartir lista con personas */
export function listasTrabajoSetCompartidosJWT(idLista, personalIds) {
    return jwtFetch('listas-trabajo/compartir', {
        method: 'POST',
        body: JSON.stringify({ idLista, personalIds }),
    });
}

/** Buscar personal para compartir */
export function listasTrabajoPersonalCompartirJWT(query) {
    return jwtFetch(`listas-trabajo/personal-compartir?q=${encodeURIComponent(query)}`);
}

// ========== HONORARIOS ==========

/** Helper genérico para llamadas JWT al backend Node.js */
async function jwtFetch(path, options = {}) {
    // Si la sesión ya está expirada, dejamos los pollings colgados silenciosamente
    // hasta que el usuario re-loguee (y se hace window.location.reload).
    // Esto evita spam de "No hay token JWT" en consola y los re-renders en cascada
    // que hacen perder foco/caracteres en el dialog de re-login.
    if (isSessionExpired) {
        return new Promise(() => { });
    }

    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) {
        triggerSessionExpired();
        return new Promise(() => { });
    }

    const res = await fetch(nodeApiPath(path), {
        ...options,
        headers: {
            "Accept": "application/json",
            ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
            "Authorization": `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });

    const data = await res.json();
    if (res.status === 401) {
        // Solo es "sesión expirada" si el 401 viene del authMiddleware por
        // token (code NO_TOKEN / INVALID_TOKEN). Los 401 de lógica de negocio
        // (un endpoint que valida id_personal y responde "No autenticado")
        // NO deben matar la sesión — antes hacían que el modal saltara en
        // cada poll de 30/60s, dando la sensación de "caduca cada 1 minuto".
        const esTokenInvalido = data?.code === 'NO_TOKEN' || data?.code === 'INVALID_TOKEN' || data?.code === 'SESSION_EXPIRED';
        if (esTokenInvalido) {
            localStorage.removeItem("SIGIF_JWT_TOKEN");
            triggerSessionExpired();
            return new Promise(() => { });
        }
        // 401 de lógica: devolvemos el body para que el caller lo maneje.
        return data;
    }
    return data;
}

// ========== FEEDBACK BETA ==========

/** Saber si el usuario actual es admin del feedback */
export const feedbackBetaSoyAdminJWT = () => jwtFetch('feedback-beta/soy-admin');

/** Crear feedback (multipart con screenshot opcional). Acepta:
 *  { categoria: 'bug'|'mejora'|'otro', mensaje, screenshot?: File|Blob } */
export async function feedbackBetaCrearJWT({ categoria, mensaje, screenshot }) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('Sin token'); }
    const form = new FormData();
    form.append('categoria', categoria);
    form.append('mensaje', mensaje);
    form.append('url', window.location.href);
    form.append('userAgent', navigator.userAgent);
    if (screenshot) form.append('screenshot', screenshot);
    const res = await fetch(nodeApiPath('feedback-beta'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form,
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

/** Listar feedback (admin) */
export const feedbackBetaListarJWT = (filtros = {}) => {
    const p = new URLSearchParams();
    if (filtros.estado) p.append('estado', filtros.estado);
    if (filtros.categoria) p.append('categoria', filtros.categoria);
    if (filtros.usuario) p.append('usuario', filtros.usuario);
    if (filtros.desde) p.append('desde', filtros.desde);
    if (filtros.hasta) p.append('hasta', filtros.hasta);
    if (filtros.page) p.append('page', filtros.page);
    if (filtros.pageSize) p.append('pageSize', filtros.pageSize);
    const qs = p.toString();
    return jwtFetch(`feedback-beta${qs ? '?' + qs : ''}`);
};
export const feedbackBetaNoLeidosJWT = () => jwtFetch('feedback-beta/no-leidos-count');
export const feedbackBetaObtenerJWT = (id) => jwtFetch(`feedback-beta/${id}`);
export const feedbackBetaActualizarJWT = (id, datos) =>
    jwtFetch(`feedback-beta/${id}`, { method: 'PATCH', body: JSON.stringify(datos) });
export const feedbackBetaEliminarJWT = (id) =>
    jwtFetch(`feedback-beta/${id}`, { method: 'DELETE' });
/** Descarga la screenshot como blob URL (uso: <img src={url}/>). El caller debe
 *  hacer URL.revokeObjectURL(url) cuando ya no se use. */
export async function feedbackBetaScreenshotBlobUrl(id) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('Sin token');
    const res = await fetch(nodeApiPath(`feedback-beta/${id}/screenshot`), {
        headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('No se pudo cargar la imagen');
    const blob = await res.blob();
    return URL.createObjectURL(blob);
}
// Admins
export const feedbackBetaListarAdminsJWT = () => jwtFetch('feedback-beta/admins');
export const feedbackBetaAgregarAdminJWT = (usuario) =>
    jwtFetch('feedback-beta/admins', { method: 'POST', body: JSON.stringify({ usuario }) });
export const feedbackBetaQuitarAdminJWT = (usuario) =>
    jwtFetch(`feedback-beta/admins/${encodeURIComponent(usuario)}`, { method: 'DELETE' });
export const feedbackBetaBuscarUsuariosJWT = (q) =>
    jwtFetch(`feedback-beta/buscar-usuarios?q=${encodeURIComponent(q || '')}`);

/** Búsqueda rápida global de personal (header) */
export function personalBuscarRapidoJWT(q, limit = 10) {
    const p = new URLSearchParams({ q: q || '', limit: String(limit) });
    return jwtFetch(`personal/buscar-rapido?${p}`);
}

/** Catálogo de columnas exportables del listado de Personal */
export const personalColumnasExportablesJWT = () => jwtFetch('personal/exportar/columnas');

/**
 * Exportar listado de Personal — descarga PDF o Excel respetando los filtros
 * actuales y la selección de columnas.
 * @param {object} opts
 * @param {object} opts.filtros - { q, idFuncion, idArea, idCondicion, externo, estado }
 * @param {string[]} opts.columnas - keys del catálogo
 * @param {'pdf'|'xlsx'} opts.formato
 * @param {'portrait'|'landscape'} [opts.orientation]  (solo PDF)
 * @param {string} [opts.titulo]
 */
export async function personalExportarJWT(opts) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('Sin token'); }
    const res = await fetch(nodeApiPath('personal/exportar'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(opts),
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.mensaje || 'Error al exportar');
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="([^"]+)"/);
    const filename = m ? m[1] : `personal_${new Date().toISOString().slice(0, 10)}.${opts.formato || 'pdf'}`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return { ok: true };
}

/** Consulta de certificados disponibles */
export function honorariosConsultaJWT(fechaDesde, fechaHasta, buscar = '', incluirSinCostas = false) {
    const params = new URLSearchParams({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
    if (buscar) params.append('buscar', buscar);
    if (incluirSinCostas) params.append('incluirSinCostas', 'true');
    return jwtFetch(`honorarios/consulta?${params}`);
}

/** Listar solicitudes del abogado */
export function honorariosSolicitudesJWT(fechaDesde, fechaHasta, idEstado) {
    const params = new URLSearchParams();
    if (fechaDesde) params.append('fecha_desde', fechaDesde);
    if (fechaHasta) params.append('fecha_hasta', fechaHasta);
    if (idEstado !== undefined && idEstado !== null) params.append('id_estado', idEstado.toString());
    return jwtFetch(`honorarios/solicitudes?${params}`);
}

/** Detalle de una solicitud con certificados */
export function honorariosSolicitudDetalleJWT(idSolicitud) {
    return jwtFetch(`honorarios/solicitud-detalle?id_solicitud=${idSolicitud}`);
}

/** Generar nueva solicitud */
export function honorariosGenerarSolicitudJWT(idsCertificados) {
    return jwtFetch('honorarios/generar-solicitud', {
        method: 'POST', body: JSON.stringify({ ids_certificados: idsCertificados }),
    });
}

/** Cerrar solicitud */
export function honorariosSolicitudCerrarJWT(idSolicitud) {
    return jwtFetch('honorarios/solicitud-cerrar', {
        method: 'POST', body: JSON.stringify({ id_solicitud: idSolicitud }),
    });
}

/** Anular solicitud */
export function honorariosSolicitudAnularJWT(idSolicitud) {
    return jwtFetch('honorarios/solicitud-anular', {
        method: 'POST', body: JSON.stringify({ id_solicitud: idSolicitud }),
    });
}

/** Añadir certificados a solicitud */
export function honorariosSolicitudAnadirJWT(idSolicitud, idsCertificados) {
    return jwtFetch('honorarios/solicitud-anadir', {
        method: 'POST', body: JSON.stringify({ id_solicitud: idSolicitud, ids_certificados: idsCertificados }),
    });
}

/** Quitar certificados de solicitud */
export function honorariosSolicitudQuitarJWT(idSolicitud, idsCertificados) {
    return jwtFetch('honorarios/solicitud-quitar', {
        method: 'POST', body: JSON.stringify({ id_solicitud: idSolicitud, ids_certificados: idsCertificados }),
    });
}

/** Resumen de honorarios */
export function honorariosResumenJWT(fechaDesde, fechaHasta) {
    const params = new URLSearchParams();
    if (fechaDesde) params.append('fecha_desde', fechaDesde);
    if (fechaHasta) params.append('fecha_hasta', fechaHasta);
    const qs = params.toString();
    return jwtFetch(`honorarios/resumen${qs ? '?' + qs : ''}`);
}

/** Dashboard de honorarios del abogado */
export function honorariosDashboardJWT() {
    return jwtFetch('honorarios/dashboard');
}

/** Admin: Listar solicitudes */
export function honorariosAdminSolicitudesJWT(fechaDesde, fechaHasta, idEstado, busqueda) {
    const params = new URLSearchParams({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
    if (idEstado !== undefined && idEstado !== null) params.append('id_estado', idEstado.toString());
    if (busqueda) params.append('busqueda', busqueda);
    return jwtFetch(`honorarios/admin/solicitudes?${params}`);
}

/** Admin: Actualizar solicitud */
export function honorariosAdminActualizarJWT(idSolicitud, tipoAccion, valor) {
    return jwtFetch('honorarios/admin/actualizar', {
        method: 'POST', body: JSON.stringify({ id_solicitud: idSolicitud, tipo_accion: tipoAccion, valor }),
    });
}

/** Admin: Asignar Exp_Adm masivo (crear lote) */
export function honorariosAdminAsignarExpMasivoJWT(idsSolicitudes, expAdm) {
    return jwtFetch('honorarios/admin/asignar-exp-masivo', {
        method: 'POST', body: JSON.stringify({ ids_solicitudes: idsSolicitudes, exp_adm: expAdm }),
    });
}

/** Admin: Cambiar estado masivo */
export function honorariosAdminCambiarEstadoMasivoJWT(idsSolicitudes, idEstado) {
    return jwtFetch('honorarios/admin/cambiar-estado-masivo', {
        method: 'POST', body: JSON.stringify({ ids_solicitudes: idsSolicitudes, id_estado: idEstado }),
    });
}

/** Admin: Listar lotes (agrupados por Exp_Adm) */
export function honorariosAdminLotesJWT(fechaDesde, fechaHasta, busqueda) {
    const params = new URLSearchParams();
    if (fechaDesde) params.append('fecha_desde', fechaDesde);
    if (fechaHasta) params.append('fecha_hasta', fechaHasta);
    if (busqueda) params.append('busqueda', busqueda);
    return jwtFetch(`honorarios/admin/lotes?${params}`);
}

/** Admin: Detalle de lote */
export function honorariosAdminLoteDetalleJWT(idExpAdm) {
    return jwtFetch(`honorarios/admin/lote-detalle?id_exp_adm=${idExpAdm}`);
}

/** Admin: Certificados global */
/**
 * Admin: certificados. Paginado de servidor — devuelve { data, total, limit, offset },
 * donde `total` es el total REAL de coincidencias, no la cantidad de la página.
 */
export function honorariosAdminCertificadosJWT(fechaDesde, fechaHasta, estado, buscar, idAbogado, limit = 50, offset = 0) {
    const params = new URLSearchParams({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
    if (estado !== undefined && estado !== null) params.append('estado', estado.toString());
    if (buscar) params.append('buscar', buscar);
    if (idAbogado) params.append('id_abogado', idAbogado.toString());
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    return jwtFetch(`honorarios/admin/certificados?${params}`);
}

/** ¿Al usuario le sirve la pantalla de Honorarios del abogado? Lo usa el Sidebar. */
export function honorariosAccesoJWT() {
    return jwtFetch('honorarios/acceso');
}

/** Admin: Dashboard */
export function honorariosAdminDashboardJWT(anio) {
    const params = anio ? `?anio=${anio}` : '';
    return jwtFetch(`honorarios/admin/dashboard${params}`);
}

/** Auditoría: Listar */
export function honorariosAuditoriaJWT(fechaDesde, fechaHasta, estado, filtro) {
    const params = new URLSearchParams({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
    if (estado !== undefined && estado !== null) params.append('estado', estado.toString());
    if (filtro) params.append('filtro', filtro);
    return jwtFetch(`honorarios/auditoria?${params}`);
}

/** Auditoría: Ejecutar cruzamiento automático */
export function honorariosAuditoriaCruzamientoJWT(fechaDesde, fechaHasta) {
    return jwtFetch('honorarios/auditoria/cruzamiento', {
        method: 'POST', body: JSON.stringify({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta }),
    });
}

/** Auditoría: Marcar registros como auditados */
export function honorariosAuditoriaAuditarJWT(ids) {
    return jwtFetch('honorarios/auditoria/auditar', {
        method: 'POST', body: JSON.stringify({ action: 'audit', ids }),
    });
}

/** Auditoría: Guardar observaciones */
export function honorariosAuditoriaObsJWT(id, referencia, protocolo) {
    return jwtFetch('honorarios/auditoria/observaciones', {
        method: 'POST', body: JSON.stringify({ id, referencia, protocolo }),
    });
}

// ==================== DOCUMENTOS (ESCRITOS) ====================

/** Obtener categorías de documentos */
export function documentosCategoriasJWT() {
    return jwtFetch('documentos/categorias');
}

/** Listar escritos con filtros */
export function documentosListarJWT(tab, q, categoria, idCategoria) {
    const params = new URLSearchParams();
    if (tab) params.append('tab', tab);
    if (q) params.append('q', q);
    if (categoria) params.append('categoria', categoria);
    if (idCategoria) params.append('idCategoria', idCategoria.toString());
    return jwtFetch(`documentos/list?${params}`);
}

/** Obtener un escrito por ID */
export function documentosGetJWT(id) {
    return jwtFetch(`documentos/get?id=${id}`);
}

/** Guardar (crear o actualizar) escrito */
export function documentosSaveJWT(data) {
    return jwtFetch('documentos/save', {
        method: 'POST', body: JSON.stringify(data),
    });
}

/** Eliminar escrito */
export function documentosDeleteJWT(id) {
    return jwtFetch(`documentos/delete?id=${id}`, { method: 'POST' });
}

/** Toggle favorito */
export function documentosFavoritoJWT(id, favorito) {
    return jwtFetch('documentos/favorito', {
        method: 'POST', body: JSON.stringify({ id, favorito }),
    });
}

/** Obtener compartidos de un escrito */
export function documentosCompartidosGetJWT(id) {
    return jwtFetch(`documentos/compartidos?id=${id}`);
}

/** Establecer compartidos */
export function documentosCompartirSetJWT(id, personalIds) {
    return jwtFetch('documentos/compartir', {
        method: 'POST', body: JSON.stringify({ id, personalIds }),
    });
}

/** Dejar de compartir conmigo */
export function documentosUnshareJWT(id) {
    return jwtFetch('documentos/unshare', {
        method: 'POST', body: JSON.stringify({ id }),
    });
}

/** Buscar personal para compartir */
export function documentosPersonalCompartirJWT(q, id) {
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (id) params.append('id', id.toString());
    return jwtFetch(`documentos/personal-compartir?${params}`);
}

/** Obtener variables disponibles para plantillas */
export function documentosVarsJWT() {
    return jwtFetch('documentos/vars');
}

/** Preview de escrito con variables reemplazadas. Acepta id (carga HTML del DB) o html directo */
export function documentosPreviewJWT({ id, html, expedienteId } = {}) {
    return jwtFetch('documentos/preview', {
        method: 'POST', body: JSON.stringify({ id, html, expedienteId }),
    });
}

/**
 * Búsqueda avanzada de expedientes con filtros complejos.
 * Devuelve { expedientes, total, limit, offset }: `total` es el total REAL de
 * coincidencias, no la cantidad devuelta. El backend topea limit en 5000.
 */
// orderBy/order: orden por columna, resuelto en el servidor (lista blanca ORDEN_EXPEDIENTES).
// Antes la función no los aceptaba: ExpedientesHome los pasaba igual y se perdían, así que
// ordenar por columna no hacía nada en cuanto había un filtro avanzado cargado.
export function busquedaAvanzadaJWT(filtros, tipoExpediente, limit = 100, offset = 0, soloIds = false, orderBy, order) {
    return jwtFetch('expedientes/busqueda-avanzada', {
        method: 'POST',
        body: JSON.stringify({
            filtros, tipoExpediente, limit, offset, soloIds,
            orderBy: orderBy || undefined, order: order || undefined,
        }),
    });
}

/** Exportar búsqueda avanzada a CSV */
export async function busquedaAvanzadaExportarJWT(expedientesIds, tipoExpediente) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }

    const res = await fetch(nodeApiPath('expedientes/busqueda-avanzada/exportar'), {
        method: 'POST',
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ expedientesIds, tipoExpediente }),
    });

    if (!res.ok) throw new Error(`Error al exportar: ${res.status}`);
    return res;
}

/** Cambiar contraseña del usuario autenticado */
export function cambiarPasswordJWT(passwordActual, passwordNueva) {
    return jwtFetch('auth/cambiar-password', {
        method: 'POST',
        body: JSON.stringify({ passwordActual, passwordNueva }),
    });
}

// ========== RECUPERACION DE USUARIO/CONTRASENA (publico) ==========
// Llama al endpoint publico sin auth. SIEMPRE devuelve mensaje generico
// independientemente de si el usuario existe (para no enumerar).
export async function solicitarResetPublic({ tipo, dniOrUsuario }) {
    try {
        const res = await fetch(nodeApiPath('auth/solicitar-reset'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({ tipo, dniOrUsuario }),
        });
        const data = await res.json();
        return data;
    } catch (error) {
        return { ok: false, mensaje: 'Error al conectar con el servidor' };
    }
}

// ========== SOLICITUDES DE RESET (admin) ==========
export const solicitudesResetListarJWT = (estado) =>
    jwtFetch(`admin/solicitudes-reset${estado ? `?estado=${encodeURIComponent(estado)}` : ''}`);

export const solicitudesResetProcesarJWT = (id, { accion, nota }) =>
    jwtFetch(`admin/solicitudes-reset/${id}/procesar`, {
        method: 'POST',
        body: JSON.stringify({ accion, nota: nota || null }),
    });

/** Info de usuario (para el módulo documentos) */
export function documentosUserInfoJWT() {
    return jwtFetch('auth/user-info');
}

// ==================== PLANTILLAS REPORTES ====================

/** Listar plantillas (tab: todos|mios|compartidos|favoritos) */
export function plantillasListarJWT(tab = 'todos') {
    return jwtFetch(`plantillas-reportes?tab=${encodeURIComponent(tab)}`);
}

/** Guardar (crear/actualizar) plantilla */
export function plantillasGuardarJWT(data) {
    return jwtFetch('plantillas-reportes', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

/** Eliminar plantilla */
export function plantillasEliminarJWT(id) {
    return jwtFetch(`plantillas-reportes/eliminar?id=${id}`, { method: 'POST' });
}

/** Toggle favorito de plantilla */
export function plantillasFavoritoJWT(id, favorito) {
    return jwtFetch('plantillas-reportes/favorito', {
        method: 'POST',
        body: JSON.stringify({ id, favorito }),
    });
}

/** Obtener compartidos de una plantilla */
export function plantillasCompartidosGetJWT(id) {
    return jwtFetch(`plantillas-reportes/compartidos?id=${id}`);
}

/** Establecer compartidos de una plantilla */
export function plantillasCompartidosSetJWT(id, personalIds) {
    return jwtFetch('plantillas-reportes/compartidos', {
        method: 'POST',
        body: JSON.stringify({ id, personalIds }),
    });
}

/** Dejar de ver plantilla compartida conmigo */
export function plantillasUnshareJWT(id) {
    return jwtFetch('plantillas-reportes/unshare', {
        method: 'POST',
        body: JSON.stringify({ id }),
    });
}

/** Buscar personal para compartir plantillas */
export function plantillasBuscarPersonalJWT(q) {
    return jwtFetch(`plantillas-reportes/buscar-personal?q=${encodeURIComponent(q)}`);
}

// ==================== TIPO MOVIMIENTOS & ESCRITOS (para AccionesMasivas) ====================

/** Obtener tipos de movimiento con formato completo */
export async function tiposMovimientosJWT() {
    const data = await getCombosJWT('tipo_movimientos', '', 500);
    return {
        ok: true,
        tipos: (data?.items || []).map(item => ({
            id: item.id,
            tipo: item.label,
            buscador_referencia: item.buscador_referencia || '',
        })),
    };
}

/** Listar escritos con todos los campos */
export async function escritosListarJWT() {
    const data = await jwtFetch('escritos?page=1&pageSize=9999');
    const items = data?.data || [];
    return {
        ok: true,
        escritos: items.map(e => ({
            id: e.Id,
            escrito: e.Escrito,
            descripcion: e.Descripcion || '',
            contenido: e.Contenido || '',
            ssrs_url: e.SSRS_URL || '',
            modelo_impresion: e.Modelo_Impresion || '',
            categoria: e.Categoria || '',
            activo: e.Activo,
            scope: e.Scope || '',
            ownerUsuario: e.OwnerUsuario || '',
            estado: e.Estado || '',
            Id_Categoria: e.Id_Categoria,
            idCategoria: e.Id_Categoria,
            categoriaNombre: e.Categoria_Nombre || '',
            Tipo_Expediente: e.Tipo_Expediente,
            Tipo_Movimiento_Sugerido: e.Tipo_Movimiento || '',
            // Keep PascalCase aliases for backward compat
            Id: e.Id,
            Escrito: e.Escrito,
            Descripcion: e.Descripcion || '',
            Contenido: e.Contenido || '',
            Activo: e.Activo,
        })),
    };
}

/** Obtener un escrito por ID (con contenido) */
export async function escritoGetJWT(id) {
    const data = await jwtFetch(`escritos/${id}`);
    return data?.data || data;
}

// ==================== READJUDICACION MASIVA ====================

export async function readjListarLotesJWT(filtros = {}) {
    const params = new URLSearchParams();
    if (filtros.estado !== undefined) params.append('estado', filtros.estado);
    if (filtros.fechaDesde) params.append('fechaDesde', filtros.fechaDesde);
    if (filtros.fechaHasta) params.append('fechaHasta', filtros.fechaHasta);
    if (filtros.modulo) params.append('modulo', filtros.modulo);
    if (filtros.busqueda) params.append('busqueda', filtros.busqueda);
    if (filtros.page) params.append('page', filtros.page);
    if (filtros.pageSize) params.append('pageSize', filtros.pageSize);
    return jwtFetch(`readjudicacion/lotes?${params.toString()}`);
}

export async function readjObtenerLoteJWT(idLote) {
    return jwtFetch(`readjudicacion/lote?id_lote=${encodeURIComponent(idLote)}`);
}

export async function readjObtenerRegistrosJWT(idLote, tipoOk = null) {
    const params = tipoOk !== null ? `&tipo_ok=${tipoOk}` : '';
    return jwtFetch(`readjudicacion/registros?id_lote=${encodeURIComponent(idLote)}${params}`);
}

export async function readjObtenerMetricasJWT() {
    return jwtFetch('readjudicacion/metricas');
}

export async function readjAuditarLoteJWT(idLote, auditarPares = true) {
    return jwtFetch('readjudicacion/auditar-lote', { method: 'POST', body: JSON.stringify({ idLote, auditarPares }) });
}

export async function readjEditarRegistroJWT(idRegistro, datos) {
    return jwtFetch('readjudicacion/editar-registro', { method: 'POST', body: JSON.stringify({ idRegistro, ...datos }) });
}

export async function readjExcluirRegistroJWT(idRegistro) {
    return jwtFetch('readjudicacion/excluir-registro', { method: 'POST', body: JSON.stringify({ idRegistro }) });
}

export async function readjReincluirRegistroJWT(idRegistro) {
    return jwtFetch('readjudicacion/reincluir-registro', { method: 'POST', body: JSON.stringify({ idRegistro }) });
}

export async function readjFinalizarAuditoriaJWT(idLote, idExpedienteAdm) {
    return jwtFetch('readjudicacion/finalizar-auditoria', { method: 'POST', body: JSON.stringify({ idLote, idExpedienteAdm }) });
}

export async function readjEliminarLoteJWT(idLote) {
    return jwtFetch('readjudicacion/eliminar-lote', { method: 'POST', body: JSON.stringify({ idLote }) });
}

export async function readjObtenerAbogadosJWT(idLote) {
    return jwtFetch(`readjudicacion/abogados?id_lote=${encodeURIComponent(idLote)}`);
}

export async function readjObtenerJuzgadosJWT(idLote) {
    return jwtFetch(`readjudicacion/juzgados?id_lote=${encodeURIComponent(idLote)}`);
}

export async function readjDesapoderarAbogadosJWT(idLote, matricula, registrosIds, caracteres = [3, 8, 26]) {
    return jwtFetch('readjudicacion/desapoderar-abogados', { method: 'POST', body: JSON.stringify({ idLote, matricula, registrosIds, caracteres }) });
}

export async function readjFinalizarDesapoderamientoJWT(idLote) {
    return jwtFetch('readjudicacion/finalizar-desapoderamiento', { method: 'POST', body: JSON.stringify({ idLote }) });
}

export async function readjAsignarAbogadosJWT(idLote, registrosIds, idAbogado, idCaracter = 3) {
    return jwtFetch('readjudicacion/asignar-abogados', { method: 'POST', body: JSON.stringify({ idLote, registrosIds, idAbogado, idCaracter }) });
}

export async function readjFinalizarAsignacionJWT(idLote) {
    return jwtFetch('readjudicacion/finalizar-asignacion', { method: 'POST', body: JSON.stringify({ idLote }) });
}

export async function readjBuscarAbogadosJWT(busqueda) {
    return jwtFetch(`readjudicacion/buscar-abogados?busqueda=${encodeURIComponent(busqueda)}`);
}

export async function readjBuscarExpedientesAdmJWT(busqueda) {
    return jwtFetch(`readjudicacion/buscar-expedientes-adm?busqueda=${encodeURIComponent(busqueda)}`);
}

export async function readjGenerarDocumentoJWT(idLote, tipoDocumento, configuracion = {}) {
    return jwtFetch('readjudicacion/generar-documento', { method: 'POST', body: JSON.stringify({ idLote, tipoDocumento, configuracion }) });
}

export async function readjGenerarDocumentosJWT(idLote, tiposDocumentos, opciones = {}) {
    return jwtFetch('readjudicacion/generar-documentos', { method: 'POST', body: JSON.stringify({ idLote, tiposDocumentos, opciones }) });
}

export async function readjActualizarEstadoJWT(idLote, estado) {
    return jwtFetch('readjudicacion/actualizar-estado', { method: 'POST', body: JSON.stringify({ idLote, estado }) });
}

export async function readjObtenerCausasAgrupadasJWT(idLote) {
    return jwtFetch(`readjudicacion/causas-agrupadas?id_lote=${encodeURIComponent(idLote)}`);
}

export async function readjCrearLoteDesdeExpedientesJWT(datos) {
    return jwtFetch('readjudicacion/crear-lote', { method: 'POST', body: JSON.stringify(datos) });
}

export async function readjObtenerRegistrosDesapoderarJWT(idLote, filtros = {}) {
    const params = new URLSearchParams();
    params.append('id_lote', idLote);
    if (filtros.matricula) params.append('matricula', filtros.matricula);
    if (filtros.idJuzgado) params.append('id_juzgado', filtros.idJuzgado);
    if (filtros.caracteres && filtros.caracteres.length > 0) params.append('caracteres', filtros.caracteres.join(','));
    return jwtFetch(`readjudicacion/registros-desapoderar?${params.toString()}`);
}

export async function readjObtenerCaracteresJWT() {
    return jwtFetch('readjudicacion/caracteres');
}

export async function readjEnumerarCartasPoderJWT(idLote) {
    return jwtFetch('readjudicacion/enumerar-cartas-poder', { method: 'POST', body: JSON.stringify({ idLote }) });
}

export async function readjObtenerFiscalEstadoJWT() {
    return jwtFetch('readjudicacion/fiscal-estado');
}

export async function readjObtenerRegistrosReciboJWT(idLote) {
    return jwtFetch(`readjudicacion/registros-recibo?id_lote=${encodeURIComponent(idLote)}`);
}

export async function readjFinalizarReadjudicacionJWT(idLote) {
    return jwtFetch('readjudicacion/finalizar-readjudicacion', { method: 'POST', body: JSON.stringify({ idLote }) });
}

export async function readjObtenerDocumentosGeneradosJWT(idLote, tipo = '') {
    const params = tipo ? `&tipo=${tipo}` : '';
    return jwtFetch(`readjudicacion/documentos-generados?id_lote=${encodeURIComponent(idLote)}${params}`);
}

export async function readjSubirPdfCartaPoderJWT(idEscrito, pdfBlob) {
    const formData = new FormData();
    formData.append('id_escrito', idEscrito);
    formData.append('archivo', pdfBlob, 'carta_poder.pdf');
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    const res = await fetch(`${nodeApiPath()}readjudicacion/subir-pdf-carta-poder`, {
        method: 'POST', body: formData,
        headers: { ...(token && { "Authorization": `Bearer ${token}` }) },
    });
    return res.json();
}

export function readjGetUrlDescargaPdfJWT(idEscrito, firmado = false) {
    return `${nodeApiPath()}readjudicacion/descargar-pdf-carta-poder?id_escrito=${idEscrito}&firmado=${firmado ? '1' : '0'}`;
}

export async function readjDescargarPdfBlobJWT(idEscrito, firmado = false) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    const url = `${nodeApiPath()}readjudicacion/descargar-pdf-carta-poder?id_escrito=${idEscrito}&firmado=${firmado ? '1' : '0'}`;
    const res = await fetch(url, {
        headers: { ...(token && { "Authorization": `Bearer ${token}` }) },
    });
    if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw new Error(errorData?.mensaje || `Error ${res.status} al descargar PDF`);
    }
    return res.blob();
}

export async function readjSubirPdfFirmadoJWT(idEscrito, pdfFile) {
    const formData = new FormData();
    formData.append('id_escrito', idEscrito);
    formData.append('archivo', pdfFile, 'carta_poder_firmada.pdf');
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    const res = await fetch(`${nodeApiPath()}readjudicacion/subir-pdf-firmado`, {
        method: 'POST', body: formData,
        headers: { ...(token && { "Authorization": `Bearer ${token}` }) },
    });
    return res.json();
}

export async function readjListarCartasPendientesFirmaJWT(idLote = null, soloSinFirmar = false) {
    const params = new URLSearchParams();
    if (idLote) params.append('id_lote', idLote);
    if (soloSinFirmar) params.append('solo_sin_firmar', '1');
    return jwtFetch(`readjudicacion/cartas-pendientes-firma?${params.toString()}`);
}

export async function readjObtenerCartaPoderDetalleJWT(idEscrito) {
    return jwtFetch(`readjudicacion/carta-poder-detalle?id_escrito=${idEscrito}`);
}

// ==================== PANEL DE FIRMA ====================

export function panelFirmaAccesoJWT() {
    return jwtFetch('panel-firma/acceso');
}

export function panelFirmaStatsJWT() {
    return jwtFetch('panel-firma/stats');
}

export function panelFirmaPendientesJWT(tipo, estado) {
    const params = new URLSearchParams();
    if (tipo) params.append('tipo', tipo);
    if (estado) params.append('estado', estado);
    return jwtFetch(`panel-firma/pendientes?${params}`);
}

export function panelFirmaDocumentoJWT(id) {
    return jwtFetch(`panel-firma/documento/${id}`);
}

export function panelFirmaContextoJWT(idExpediente) {
    return jwtFetch(`panel-firma/contexto-expediente/${idExpediente}`);
}

export function panelFirmaSolicitarJWT(data) {
    return jwtFetch('panel-firma/solicitar-firma', {
        method: 'POST', body: JSON.stringify(data),
    });
}

export function panelFirmaAprobarJWT(id) {
    return jwtFetch(`panel-firma/aprobar/${id}`, { method: 'POST' });
}

export function panelFirmaRechazarJWT(id, motivo) {
    return jwtFetch(`panel-firma/rechazar/${id}`, {
        method: 'POST', body: JSON.stringify({ motivo }),
    });
}

export async function panelFirmaSubirPdfJWT(id, pdfBlob) {
    const formData = new FormData();
    formData.append('archivo', pdfBlob, 'documento.pdf');
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    const res = await fetch(nodeApiPath(`panel-firma/subir-pdf/${id}`), {
        method: 'POST', body: formData,
        headers: { ...(token && { "Authorization": `Bearer ${token}` }) },
    });
    return res.json();
}

export async function panelFirmaSubirPdfFirmadoJWT(id, pdfFile) {
    const formData = new FormData();
    formData.append('archivo', pdfFile, 'documento_firmado.pdf');
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    const res = await fetch(nodeApiPath(`panel-firma/subir-pdf-firmado/${id}`), {
        method: 'POST', body: formData,
        headers: { ...(token && { "Authorization": `Bearer ${token}` }) },
    });
    return res.json();
}

export async function panelFirmaDescargarPdfJWT(id, firmado = false) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    const res = await fetch(nodeApiPath(`panel-firma/descargar-pdf/${id}?firmado=${firmado ? '1' : '0'}`), {
        headers: { ...(token && { "Authorization": `Bearer ${token}` }) },
    });
    if (!res.ok) throw new Error('Error al descargar PDF');
    return res.blob();
}

export function panelFirmaDelegadosJWT() {
    return jwtFetch('panel-firma/delegados');
}

export function panelFirmaAgregarDelegadoJWT(usuario) {
    return jwtFetch('panel-firma/delegados', {
        method: 'POST', body: JSON.stringify({ usuario }),
    });
}

export function panelFirmaEliminarDelegadoJWT(id) {
    return jwtFetch(`panel-firma/delegados/${id}`, { method: 'DELETE' });
}

// export por defecto
const endpoints = {
    loginJWT,
    userInfoJWT,
    permisosUsuarioJWT,
    logoutJWT,
    refreshJWT,
    dashboardStatsJWT,
    getCombosJWT,
    notificacionesListarJWT,
    notificacionMarcarLeidaJWT,
    notificacionesMarcarTodasLeidasJWT,
    notificacionEliminarJWT,
    expedientesHomeJWT,
    expedientesBusquedaUnificadaJWT,
    expedienteDetalleJWT,
    expedientePartesJWT,
    crearParteJWT,
    editarParteJWT,
    eliminarParteJWT,
    crearContraparteJWT,
    expedienteMovimientosJWT,
    crearMovimientoJWT,
    editarMovimientoJWT,
    eliminarMovimientoJWT,
    expedienteVinculadosJWT,
    crearVinculadoJWT,
    downloadVinculadoJWT,
    getVinculadoJWT,
    eliminarVinculadoJWT,
    listasTrabajoObtenerJWT,
    listasTrabajoObtenerUnaJWT,
    listasTrabajoCrearJWT,
    listasTrabajoActualizarJWT,
    listasTrabajoEliminarJWT,
    listasTrabajoAgregarExpedientesJWT,
    listasTrabajoEliminarExpedientesJWT,
    listasTrabajoMarcarVistoJWT,
    listasTrabajoToggleFavoritaJWT,
    listasTrabajoActualizarNotasJWT,
    honorariosAuditoriaAuditarJWT,
    honorariosAuditoriaObsJWT,
    documentosCategoriasJWT,
    documentosListarJWT,
    documentosGetJWT,
    documentosSaveJWT,
    documentosDeleteJWT,
    documentosFavoritoJWT,
    documentosCompartidosGetJWT,
    documentosCompartirSetJWT,
    documentosUnshareJWT,
    documentosPersonalCompartirJWT,
    documentosVarsJWT,
    documentosPreviewJWT,
    documentosUserInfoJWT,
    expedienteAreasConjuntasJWT,
    agregarAreaConjuntaJWT,
    eliminarAreaConjuntaJWT,
    expedienteRiesgoRelevanciaJWT,
    guardarRiesgoRelevanciaJWT,
    expedienteEmbargosJWT,
    embargoByMovimientoJWT,
    crearCertificadoJWT,
    editarCertificadoJWT,
    eliminarCertificadoJWT,
    guardarRadicacionJWT,
    eliminarRadicacionHistorialJWT,
    expedienteConcursoJWT,
    guardarConcursoJWT,
    expedienteAcreedoresJWT,
    crearAcreedorJWT,
    editarAcreedorJWT,
    eliminarAcreedorJWT,
    crearInmuebleJWT,
    editarInmuebleJWT,
    eliminarInmuebleJWT,
    expedienteTitularesJWT,
    crearTitularJWT,
    editarTitularJWT,
    eliminarTitularJWT,
    siguienteDniFicticioJWT,
    buscarContrapartePorDniJWT,
    expedienteVisibilidadJWT,
    editarExpedienteJWT,
    expedienteAutosJWT,
    guardarAutoJWT,
    expedienteCartaPoderJWT,
    readjListarLotesJWT,
    readjObtenerLoteJWT,
    readjObtenerRegistrosJWT,
    readjObtenerMetricasJWT,
    readjAuditarLoteJWT,
    readjEditarRegistroJWT,
    readjExcluirRegistroJWT,
    readjReincluirRegistroJWT,
    readjFinalizarAuditoriaJWT,
    readjEliminarLoteJWT,
    readjObtenerAbogadosJWT,
    readjObtenerJuzgadosJWT,
    readjDesapoderarAbogadosJWT,
    readjFinalizarDesapoderamientoJWT,
    readjAsignarAbogadosJWT,
    readjFinalizarAsignacionJWT,
    readjBuscarAbogadosJWT,
    readjBuscarExpedientesAdmJWT,
    readjGenerarDocumentoJWT,
    readjGenerarDocumentosJWT,
    readjActualizarEstadoJWT,
    readjObtenerCausasAgrupadasJWT,
    readjCrearLoteDesdeExpedientesJWT,
    readjObtenerRegistrosDesapoderarJWT,
    readjObtenerCaracteresJWT,
    readjEnumerarCartasPoderJWT,
    readjObtenerFiscalEstadoJWT,
    readjObtenerRegistrosReciboJWT,
    readjFinalizarReadjudicacionJWT,
    readjObtenerDocumentosGeneradosJWT,
    readjSubirPdfCartaPoderJWT,
    readjGetUrlDescargaPdfJWT,
    readjDescargarPdfBlobJWT,
    readjSubirPdfFirmadoJWT,
    readjListarCartasPendientesFirmaJWT,
    readjObtenerCartaPoderDetalleJWT,
    escritosComboJWT,
    panelFirmaAccesoJWT,
    panelFirmaStatsJWT,
    panelFirmaPendientesJWT,
    panelFirmaDocumentoJWT,
    panelFirmaContextoJWT,
    panelFirmaSolicitarJWT,
    panelFirmaAprobarJWT,
    panelFirmaRechazarJWT,
    panelFirmaSubirPdfJWT,
    panelFirmaSubirPdfFirmadoJWT,
    panelFirmaDescargarPdfJWT,
    panelFirmaDelegadosJWT,
    panelFirmaAgregarDelegadoJWT,
    panelFirmaEliminarDelegadoJWT,
};
export default endpoints;

/**
 * escritosComboJWT - Obtener escritos/plantillas para combo de movimientos
 */
export async function escritosComboJWT() {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    if (!token) { triggerSessionExpired(); throw new Error("No hay token JWT"); }
    const res = await fetch(nodeApiPath("escritos/combo"), {
        headers: {
            "Accept": "application/json",
            "Authorization": `Bearer ${token}`,
        },
    });
    return await res.json();
}

// ===== PASES =====

/** Obtener ubicación actual del expediente */
export function ubicacionExpedienteJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/ubicacion`);
}

/** Obtener timeline de pases de un expediente */
export function pasesTimelineJWT(idExpediente) {
    return jwtFetch(`expedientes/${idExpediente}/pases`);
}

/** Enviar pase (crear) */
export function pasesEnviarJWT(idExpediente, data) {
    return jwtFetch(`expedientes/${idExpediente}/pases`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

/** Aceptar pase */
export function pasesAceptarJWT(idPase, observaciones) {
    return jwtFetch(`pases/${idPase}/aceptar`, {
        method: 'PUT',
        body: JSON.stringify({ observaciones }),
    });
}

/** Rechazar pase */
export function pasesRechazarJWT(idPase, motivo) {
    return jwtFetch(`pases/${idPase}/rechazar`, {
        method: 'PUT',
        body: JSON.stringify({ motivo }),
    });
}

/** Bandeja de entrada de pases */
export function pasesBandejaJWT() {
    return jwtFetch('pases/bandeja');
}

/** Count de pases pendientes (para badge) */
export function pasesBandejaCountJWT() {
    return jwtFetch('pases/bandeja/count');
}

/** Prioridades de pases */
export function pasesPrioridadesJWT() {
    return jwtFetch('pases/prioridades');
}

// ============================================================================
// CARGA MASIVA
// ============================================================================

export function cargaMasivaListarLotesJWT(filtros = {}) {
    const p = new URLSearchParams();
    if (filtros.estado !== undefined && filtros.estado !== '') p.append('estado', filtros.estado);
    if (filtros.idModulo) p.append('id_modulo', filtros.idModulo);
    if (filtros.idReparticion) p.append('id_reparticion', filtros.idReparticion);
    if (filtros.fechaDesde) p.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) p.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.busqueda) p.append('busqueda', filtros.busqueda);
    if (filtros.page) p.append('page', filtros.page);
    if (filtros.pageSize) p.append('page_size', filtros.pageSize);
    return jwtFetch(`carga-masiva/lotes?${p.toString()}`);
}

export function cargaMasivaObtenerLoteJWT(idLote) {
    return jwtFetch(`carga-masiva/lote?id_lote=${encodeURIComponent(idLote)}`);
}

export function cargaMasivaCrearLoteJWT({ idModulo, idReparticion, observaciones, idExpedienteAdministrativo, cantidadEsperada }) {
    return jwtFetch('carga-masiva/crear-lote', {
        method: 'POST',
        body: JSON.stringify({
            id_modulo: idModulo,
            id_reparticion: idReparticion,
            observaciones,
            id_expediente_administrativo: idExpedienteAdministrativo,
            cantidad_esperada: cantidadEsperada,
        }),
    });
}

// ===== LOTES CÓDIGOS DE BARRA / CERTIFICADOS TRIBUTARIOS (push DGR) =====
export function barrasColaAuditarJWT(filtro = {}) {
    const p = new URLSearchParams();
    if (filtro.campo && filtro.valor) { p.append('campo', filtro.campo); p.append('valor', filtro.valor); }
    return jwtFetch(`carga-masiva/barras/cola-auditar?${p.toString()}`);
}
export function barrasAuditarExpedienteJWT(expAdm, limite = 300) {
    return jwtFetch('carga-masiva/barras/auditar-expediente', {
        method: 'POST', body: JSON.stringify({ exp_adm: expAdm, limite }),
    });
}
export function barrasColaRevisarJWT(estado = 0, filtro = {}) {
    const p = new URLSearchParams();
    p.append('estado', estado);
    if (filtro.campo && filtro.valor) { p.append('campo', filtro.campo); p.append('valor', filtro.valor); }
    return jwtFetch(`carga-masiva/barras/cola-revisar?${p.toString()}`);
}
export function barrasDetalleExpJWT(expAdm, { campo, valor, page = 1, pageSize = 50 } = {}) {
    const p = new URLSearchParams();
    p.append('exp_adm', expAdm);
    if (campo && valor) { p.append('campo', campo); p.append('valor', valor); }
    p.append('page', page);
    p.append('page_size', pageSize);
    return jwtFetch(`carga-masiva/barras/detalle-exp?${p.toString()}`);
}
export function barrasVerPdfCertJWT(idCert) {
    return jwtFetch(`carga-masiva/barras/ver-pdf-cert?id_cert=${encodeURIComponent(idCert)}`);
}
export function barrasFinalizarRecepcionJWT(expAdm) {
    return jwtFetch('carga-masiva/barras/finalizar-recepcion', {
        method: 'POST', body: JSON.stringify({ exp_adm: expAdm }),
    });
}
export function barrasEnviarErroresJWT(expAdm) {
    return jwtFetch('carga-masiva/barras/enviar-errores', {
        method: 'POST', body: JSON.stringify({ exp_adm: expAdm }),
    });
}
export function barrasValidadosParaLoteJWT(expAdm, filtros = {}) {
    const p = new URLSearchParams();
    p.append('exp_adm', expAdm);
    if (filtros.impuesto) p.append('impuesto', filtros.impuesto);
    if (filtros.codigoPostal) p.append('codigo_postal', filtros.codigoPostal);
    if (filtros.montoMin != null && filtros.montoMin !== '') p.append('monto_min', filtros.montoMin);
    if (filtros.montoMax != null && filtros.montoMax !== '') p.append('monto_max', filtros.montoMax);
    return jwtFetch(`carga-masiva/barras/validados-para-lote?${p.toString()}`);
}
export function barrasGenerarLoteJWT({ expAdm, idsCertificados, observaciones }) {
    return jwtFetch('carga-masiva/barras/generar-lote', {
        method: 'POST',
        body: JSON.stringify({ exp_adm: expAdm, ids_certificados: idsCertificados, observaciones }),
    });
}

export function cargaMasivaEliminarLoteJWT(idLote) {
    return jwtFetch('carga-masiva/eliminar-lote', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaActualizarEstadoJWT(idLote, estado) {
    return jwtFetch('carga-masiva/actualizar-estado', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, estado }),
    });
}

export function cargaMasivaListarDetalleJWT(idLote, estadoOk) {
    const p = new URLSearchParams({ id_lote: idLote });
    if (estadoOk !== undefined && estadoOk !== '') p.append('estado_ok', estadoOk);
    return jwtFetch(`carga-masiva/detalle?${p.toString()}`);
}

export function cargaMasivaAuditarFilaJWT({ idDetalle, idLote, estadoOk, observaciones }) {
    return jwtFetch('carga-masiva/auditar-fila', {
        method: 'POST',
        body: JSON.stringify({ id_detalle: idDetalle, id_lote: idLote, estado_ok: estadoOk, observaciones }),
    });
}

export function cargaMasivaMetricasJWT(idModulo) {
    const p = idModulo ? `?id_modulo=${idModulo}` : '';
    return jwtFetch(`carga-masiva/metricas${p}`);
}

export function cargaMasivaAuditoriaJWT(idLote) {
    return jwtFetch(`carga-masiva/auditoria?id_lote=${encodeURIComponent(idLote)}`);
}

export function cargaMasivaSiguienteCartaPoderJWT(idModulo) {
    return jwtFetch('carga-masiva/siguiente-carta-poder', {
        method: 'POST',
        body: JSON.stringify({ id_modulo: idModulo }),
    });
}

export function cargaMasivaEditarFilaJWT(idLote, idDetalle, campos) {
    return jwtFetch('carga-masiva/editar-fila', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, id_detalle: idDetalle, campos }),
    });
}

export function cargaMasivaBulkMarcarCorrectasJWT(idLote) {
    return jwtFetch('carga-masiva/bulk-marcar-correctas', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaFinalizarAuditoriaJWT(idLote) {
    return jwtFetch('carga-masiva/finalizar-auditoria', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaListarAbogadosJWT(busqueda) {
    const p = busqueda ? `?busqueda=${encodeURIComponent(busqueda)}` : '';
    return jwtFetch(`carga-masiva/abogados${p}`);
}

export function cargaMasivaAbogadosLoteJWT(idLote) {
    return jwtFetch(`carga-masiva/abogados-lote?id_lote=${encodeURIComponent(idLote)}`);
}

export function cargaMasivaSortearAbogadosJWT(idLote, idsAbogados) {
    return jwtFetch('carga-masiva/sortear-abogados', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, ids_abogados: idsAbogados }),
    });
}

export function cargaMasivaAsignarAbogadoManualJWT(idLote, idDetalle, idAbogado) {
    return jwtFetch('carga-masiva/asignar-abogado-manual', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, id_detalle: idDetalle, id_abogado: idAbogado }),
    });
}

export function cargaMasivaDesasignarAbogadoJWT(idLote, idDetalle) {
    return jwtFetch('carga-masiva/desasignar-abogado', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, id_detalle: idDetalle }),
    });
}

export function cargaMasivaFinalizarAsignacionJWT(idLote) {
    return jwtFetch('carga-masiva/finalizar-asignacion', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

// ===== PLANTILLAS (gestor propio) =====

export function cargaMasivaListarPlantillasJWT() {
    return jwtFetch('carga-masiva/plantillas');
}

export function cargaMasivaObtenerPlantillaJWT(id) {
    return jwtFetch(`carga-masiva/plantillas/${id}`);
}

export function cargaMasivaActualizarPlantillaJWT(id, datos) {
    return jwtFetch(`carga-masiva/plantillas/${id}`, {
        method: 'PUT',
        body: JSON.stringify(datos),
    });
}

// ===== CO-APODERADOS =====

export function cargaMasivaApoderadosFilaJWT(idDetalle) {
    return jwtFetch(`carga-masiva/apoderados-fila?id_detalle=${idDetalle}`);
}

export function cargaMasivaAgregarCoApoderadoJWT(idLote, idDetalle, idAbogado) {
    return jwtFetch('carga-masiva/agregar-co-apoderado', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, id_detalle: idDetalle, id_abogado: idAbogado }),
    });
}

export function cargaMasivaQuitarCoApoderadoJWT(idLote, idDetalle, idAbogado) {
    return jwtFetch('carga-masiva/quitar-co-apoderado', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, id_detalle: idDetalle, id_abogado: idAbogado }),
    });
}

// ===== FIRMA DIGITAL =====

export function cargaMasivaEnviarAFirmaJWT(idLote) {
    return jwtFetch('carga-masiva/enviar-a-firma', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaSincronizarFirmasJWT(idLote) {
    return jwtFetch('carga-masiva/sincronizar-firmas', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaResumenFirmaJWT(idLote) {
    return jwtFetch(`carga-masiva/resumen-firma?id_lote=${encodeURIComponent(idLote)}`);
}

// ===== FIRMA CIRCULAR DEMANDA =====

export function cargaMasivaEnviarDemandasAFirmaJWT(idLote) {
    return jwtFetch('carga-masiva/enviar-demandas-a-firma', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaSincronizarFirmasDemandaJWT(idLote) {
    return jwtFetch('carga-masiva/sincronizar-firmas-demanda', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaResumenFirmaDemandaJWT(idLote) {
    return jwtFetch(`carga-masiva/resumen-firma-demanda?id_lote=${encodeURIComponent(idLote)}`);
}

export function cargaMasivaFirmasDemandaJWT(idDetalle) {
    return jwtFetch(`carga-masiva/firmas-demanda?id_detalle=${idDetalle}`);
}

// ===== PANEL FIRMA ABOGADOS =====

export function cargaMasivaMisDemandasPendientesJWT() {
    return jwtFetch('carga-masiva/mis-demandas-pendientes');
}

export function cargaMasivaMiCountFirmasPendientesJWT() {
    return jwtFetch('carga-masiva/mi-count-firmas-pendientes');
}

export async function cargaMasivaDescargarDemandaFirmaBlob(idDocumento) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const res = await fetch(nodeApiPath(`carga-masiva/descargar-demanda-firma?id_documento=${idDocumento}`), {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.blob();
}

// ===== INTEGRACIÓN PJ =====

export function cargaMasivaPreviewJsonPJJWT(idLote) {
    return jwtFetch(`carga-masiva/preview-json-pj?id_lote=${encodeURIComponent(idLote)}`);
}

export function cargaMasivaEnviarAlPJJWT(idLote) {
    return jwtFetch('carga-masiva/enviar-al-pj', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaReenviarRechazadasPJJWT(idLote) {
    return jwtFetch('carga-masiva/reenviar-rechazadas-pj', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaForzarRechazoSimuladoJWT(idLote, idDetalle, motivo) {
    return jwtFetch('carga-masiva/forzar-rechazo-simulado', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, id_detalle: idDetalle, motivo }),
    });
}

export function cargaMasivaPasarASigifJWT(idLote) {
    return jwtFetch('carga-masiva/pasar-a-sigif', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export async function cargaMasivaExportarPDFBlob(idLote) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No token'); }
    const url = `${nodeApiPath('carga-masiva/exportar-pdf')}?id_lote=${encodeURIComponent(idLote)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.blob();
}

export function cargaMasivaConsultarEstadoPJJWT(idLote, idLotePJ) {
    return jwtFetch('carga-masiva/consultar-estado-pj', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, id_lote_pj: idLotePJ }),
    });
}

export function cargaMasivaResetSimulacionPJJWT(idLote) {
    return jwtFetch('carga-masiva/reset-simulacion-pj', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote }),
    });
}

export function cargaMasivaEnviosPJJWT(idLote) {
    return jwtFetch(`carga-masiva/envios-pj?id_lote=${encodeURIComponent(idLote)}`);
}

export async function cargaMasivaSubirDemandaFirmadaJWT(idDocumento, pdfFile) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const form = new FormData();
    form.append('file', pdfFile);
    const res = await fetch(nodeApiPath(`carga-masiva/subir-demanda-firmada/${idDocumento}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

export function cargaMasivaGenerarDocumentosJWT(idLote, tipos, soloFaltantes = false) {
    return jwtFetch('carga-masiva/generar-documentos', {
        method: 'POST',
        body: JSON.stringify({ id_lote: idLote, tipos, solo_faltantes: soloFaltantes }),
    });
}

export function cargaMasivaDescargarPDFUrl(idDetalle, tipo) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    const base = nodeApiPath(`carga-masiva/descargar-pdf?id_detalle=${idDetalle}&tipo=${tipo}`);
    return { url: base, token };
}

export async function cargaMasivaDescargarPDFBlob(idDetalle, tipo) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const res = await fetch(nodeApiPath(`carga-masiva/descargar-pdf?id_detalle=${idDetalle}&tipo=${tipo}`), {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.blob();
}

export async function cargaMasivaImportarExcelJWT(idLote, file, { reemplazar = false, pdfs = [] } = {}) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const form = new FormData();
    form.append('id_lote', idLote);
    form.append('reemplazar', reemplazar ? 'true' : 'false');
    form.append('file', file);
    for (const pdf of pdfs) form.append('pdfs', pdf);
    const res = await fetch(nodeApiPath('carga-masiva/importar-excel'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
    });
    if (res.status === 401) {
        triggerSessionExpired();
        throw new Error('Token expirado');
    }
    return await res.json();
}

export async function cargaMasivaSubirPdfsJWT(idLote, pdfs) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const form = new FormData();
    form.append('id_lote', idLote);
    for (const pdf of pdfs) form.append('pdfs', pdf);
    const res = await fetch(nodeApiPath('carga-masiva/subir-pdfs-certificados'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

export async function cargaMasivaAsignarPdfManualJWT(idLote, idDetalle, nombreArchivo) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const res = await fetch(nodeApiPath('carga-masiva/asignar-pdf-manual'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_lote: idLote, id_detalle: idDetalle, nombre_archivo: nombreArchivo }),
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

export async function cargaMasivaReprocesarPdfsStagingJWT(idLote) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const res = await fetch(nodeApiPath('carga-masiva/reprocesar-pdfs-staging'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_lote: idLote }),
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

export async function cargaMasivaEliminarPdfStagingJWT(idLote, nombreArchivo) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const res = await fetch(nodeApiPath('carga-masiva/eliminar-pdf-staging'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_lote: idLote, nombre_archivo: nombreArchivo }),
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

export async function cargaMasivaListarPdfsStagingJWT(idLote) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const url = `${nodeApiPath('carga-masiva/pdfs-staging')}?id_lote=${encodeURIComponent(idLote)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

// ========== ACTUALIZACIÓN DGR — Migrar Planes de Pago ==========

export function dgrPlanesPagoMigrarJWT(fechaDesde, fechaHasta) {
    return jwtFetch('dgr/planes-pago/migrar', {
        method: 'POST',
        body: JSON.stringify({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta }),
    });
}

export function dgrPlanesPagoListarLotesJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.usuario) qs.append('usuario', filtros.usuario);
    if (filtros.cruzado !== undefined) qs.append('cruzado', filtros.cruzado);
    if (filtros.procesado !== undefined) qs.append('procesado', filtros.procesado);
    if (filtros.page) qs.append('page', filtros.page);
    if (filtros.pageSize) qs.append('page_size', filtros.pageSize);
    return jwtFetch(`dgr/planes-pago/lotes?${qs.toString()}`);
}

export function dgrPlanesPagoListarDetalleJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.idLote) qs.append('id_lote', filtros.idLote);
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.resultado) qs.append('resultado', filtros.resultado);
    if (filtros.refNroPlan) qs.append('ref_nro_plan', filtros.refNroPlan);
    if (filtros.letra) qs.append('letra', filtros.letra);
    if (filtros.numero) qs.append('numero', filtros.numero);
    if (filtros.page) qs.append('page', filtros.page);
    if (filtros.pageSize) qs.append('page_size', filtros.pageSize);
    return jwtFetch(`dgr/planes-pago/detalle?${qs.toString()}`);
}

export function dgrPlanesPagoMetricasJWT() {
    return jwtFetch('dgr/planes-pago/metricas');
}

// ========== ACTUALIZACIÓN DGR — Cambios detectados (diferencias) ==========

export function dgrDiferenciasListarJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.estado) qs.append('estado', filtros.estado);
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.page) qs.append('page', filtros.page);
    if (filtros.pageSize) qs.append('page_size', filtros.pageSize);
    return jwtFetch(`dgr/planes-pago/diferencias?${qs.toString()}`);
}
export function dgrDiferenciasContadoresJWT() {
    return jwtFetch('dgr/planes-pago/diferencias/contadores');
}
export function dgrDiferenciaAceptarJWT(id, observacion) {
    return jwtFetch(`dgr/planes-pago/diferencias/${id}/aceptar`, {
        method: 'POST',
        body: JSON.stringify({ observacion }),
        headers: { 'Content-Type': 'application/json' },
    });
}
export function dgrDiferenciaRechazarJWT(id, observacion) {
    return jwtFetch(`dgr/planes-pago/diferencias/${id}/rechazar`, {
        method: 'POST',
        body: JSON.stringify({ observacion }),
        headers: { 'Content-Type': 'application/json' },
    });
}
export function dgrDiferenciasAceptarBatchJWT(ids) {
    return jwtFetch('dgr/planes-pago/diferencias/aceptar-batch', {
        method: 'POST',
        body: JSON.stringify({ ids }),
        headers: { 'Content-Type': 'application/json' },
    });
}
export function dgrDiferenciasRechazarBatchJWT(ids) {
    return jwtFetch('dgr/planes-pago/diferencias/rechazar-batch', {
        method: 'POST',
        body: JSON.stringify({ ids }),
        headers: { 'Content-Type': 'application/json' },
    });
}
export function dgrDiferenciaRevertirJWT(id, observacion) {
    return jwtFetch(`dgr/planes-pago/diferencias/${id}/revertir`, {
        method: 'POST',
        body: JSON.stringify({ observacion }),
        headers: { 'Content-Type': 'application/json' },
    });
}
export function dgrDiferenciasExportarJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.estado) qs.append('estado', filtros.estado);
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    return jwtFetch(`dgr/planes-pago/diferencias/exportar?${qs.toString()}`);
}
export function dgrDiferenciasPurgarRuidoJWT() {
    return jwtFetch('dgr/planes-pago/diferencias/purgar-ruido', {
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'Content-Type': 'application/json' },
    });
}

// ========== ACTUALIZACIÓN DGR — Pasaje a Ejecuciones ==========

export function dgrPasajeListarJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.numeroPlan) qs.append('numero_plan', filtros.numeroPlan);
    if (filtros.letra) qs.append('letra', filtros.letra);
    if (filtros.numero) qs.append('numero', filtros.numero);
    if (filtros.idLote) qs.append('id_lote', filtros.idLote);
    if (filtros.idDetalle) qs.append('id_detalle', filtros.idDetalle);
    if (filtros.estado) qs.append('estado', filtros.estado);
    if (filtros.soloConciliados) qs.append('solo_conciliados', '1');
    if (filtros.page) qs.append('page', filtros.page);
    if (filtros.pageSize) qs.append('page_size', filtros.pageSize);
    return jwtFetch(`dgr/pasaje/listar?${qs.toString()}`);
}

// Lotes de planes de pago con agregados (vista "por lote diario")
export function dgrPasajeLotesJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.soloConPendientes) qs.append('solo_con_pendientes', '1');
    if (filtros.page) qs.append('page', filtros.page);
    if (filtros.pageSize) qs.append('page_size', filtros.pageSize);
    return jwtFetch(`dgr/pasaje/lotes?${qs.toString()}`);
}

export function dgrPasajeMetricasJWT() {
    return jwtFetch('dgr/pasaje/metricas');
}

export function dgrPasajeEjecutarJWT(ids) {
    return jwtFetch('dgr/pasaje/pasar', {
        method: 'POST',
        body: JSON.stringify({ ids }),
    });
}

// "Actualizar DGR": pasa a auditoría todos los conciliados (V) pendientes,
// opcionalmente filtrado por lote o rango de fechas. Sin selección manual.
export function dgrPasajeActualizarJWT({ idLote, fechaDesde, fechaHasta } = {}) {
    return jwtFetch('dgr/pasaje/actualizar', {
        method: 'POST',
        body: JSON.stringify({
            id_lote: idLote || undefined,
            fecha_desde: fechaDesde || undefined,
            fecha_hasta: fechaHasta || undefined,
        }),
    });
}

// "Crear expediente igual": fuerza creación de expediente para un plan
// conciliado pendiente (botón manual de la lista de pendientes de expediente).
export function dgrPasajeForzarCrearJWT(idDetalle) {
    return jwtFetch('dgr/pasaje/forzar-crear', {
        method: 'POST',
        body: JSON.stringify({ id_detalle: idDetalle }),
    });
}
// Vincular ahora: ejecuta vinculación masiva de planes Incompletos contra
// expedientes ya existentes (equivalente al "Cruzar Datos" del legacy).
export function dgrPasajeVincularAhoraJWT(fechaDesde, fechaHasta) {
    return jwtFetch('dgr/pasaje/vincular-ahora', {
        method: 'POST',
        body: JSON.stringify({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta }),
        headers: { 'Content-Type': 'application/json' },
    });
}
// Detalle COMPLETO de un plan (ciclo end-to-end: import + conciliación +
// vinculación + auditoría + cobranza). Alimenta el super-drawer "Detalle del Plan".
export function dgrPasajeDetalleCompletoJWT(idDetalle) {
    return jwtFetch(`dgr/pasaje/detalle-completo/${idDetalle}`);
}

// ========== CONCILIACIÓN EXTERNA (BSJ + RA + Motor + Auditoría) ==========

export async function conciliacionBsjImportarJWT(file, plusPagos) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const form = new FormData();
    form.append('file', file);
    form.append('plus_pagos', plusPagos ? '1' : '0');
    const res = await fetch(nodeApiPath('conciliacion-externa/bsj/importar'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}
export function conciliacionBsjAnularJWT(id) {
    return jwtFetch(`conciliacion-externa/bsj/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function conciliacionBsjListarJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.idConciliacion) qs.append('id_conciliacion', filtros.idConciliacion);
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.plusPagos !== undefined && filtros.plusPagos !== '') qs.append('plus_pagos', filtros.plusPagos);
    if (filtros.solicitud) qs.append('solicitud', filtros.solicitud);
    if (filtros.page) qs.append('page', filtros.page);
    if (filtros.pageSize) qs.append('page_size', filtros.pageSize);
    return jwtFetch(`conciliacion-externa/bsj?${qs.toString()}`);
}
export function conciliacionBsjDetalleJWT(id, page = 1, pageSize = 50) {
    return jwtFetch(`conciliacion-externa/bsj/${encodeURIComponent(id)}/detalle?page=${page}&page_size=${pageSize}`);
}

export async function conciliacionRaImportarJWT(file, fecha) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No token');
    const form = new FormData();
    form.append('file', file);
    form.append('fecha', fecha);
    const res = await fetch(nodeApiPath('conciliacion-externa/ra/importar'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}
export function conciliacionRaAnularJWT(id) {
    return jwtFetch(`conciliacion-externa/ra/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function conciliacionRaListarJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.idConciliacion) qs.append('id_conciliacion', filtros.idConciliacion);
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.solicitud) qs.append('solicitud', filtros.solicitud);
    if (filtros.page) qs.append('page', filtros.page);
    if (filtros.pageSize) qs.append('page_size', filtros.pageSize);
    return jwtFetch(`conciliacion-externa/ra?${qs.toString()}`);
}
export function conciliacionRaDetalleJWT(id, page = 1, pageSize = 50) {
    return jwtFetch(`conciliacion-externa/ra/${encodeURIComponent(id)}/detalle?page=${page}&page_size=${pageSize}`);
}

export function conciliacionMotorEjecutarJWT(fechaDesde, fechaHasta) {
    return jwtFetch('conciliacion-externa/conciliar', {
        method: 'POST',
        body: JSON.stringify({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta }),
    });
}

export function conciliacionAuditoriaSolicitudJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.estado) qs.append('estado', filtros.estado);
    if (filtros.origen) qs.append('origen', filtros.origen);
    if (filtros.solicitud) qs.append('solicitud', filtros.solicitud);
    if (filtros.page) qs.append('page', filtros.page);
    if (filtros.pageSize) qs.append('page_size', filtros.pageSize);
    return jwtFetch(`conciliacion-externa/auditoria/solicitud?${qs.toString()}`);
}
export function conciliacionAuditoriaCertificadoJWT(filtros = {}) {
    const qs = new URLSearchParams();
    if (filtros.fechaDesde) qs.append('fecha_desde', filtros.fechaDesde);
    if (filtros.fechaHasta) qs.append('fecha_hasta', filtros.fechaHasta);
    if (filtros.estado) qs.append('estado', filtros.estado);
    if (filtros.origen) qs.append('origen', filtros.origen);
    if (filtros.solicitud) qs.append('solicitud', filtros.solicitud);
    if (filtros.certificado) qs.append('certificado', filtros.certificado);
    if (filtros.page) qs.append('page', filtros.page);
    if (filtros.pageSize) qs.append('page_size', filtros.pageSize);
    return jwtFetch(`conciliacion-externa/auditoria/certificado?${qs.toString()}`);
}
export function conciliacionMetricasJWT(fechaDesde, fechaHasta, origen, solicitud) {
    const qs = new URLSearchParams();
    if (fechaDesde) qs.append('fecha_desde', fechaDesde);
    if (fechaHasta) qs.append('fecha_hasta', fechaHasta);
    if (origen) qs.append('origen', origen);
    if (solicitud) qs.append('solicitud', solicitud);
    return jwtFetch(`conciliacion-externa/auditoria/metricas?${qs.toString()}`);
}
export function conciliacionEstadosJWT() {
    return jwtFetch('conciliacion-externa/auditoria/estados');
}
export function conciliacionDashboardJWT(fechaDesde, fechaHasta) {
    const qs = new URLSearchParams();
    if (fechaDesde) qs.append('fecha_desde', fechaDesde);
    if (fechaHasta) qs.append('fecha_hasta', fechaHasta);
    return jwtFetch(`conciliacion-externa/auditoria/dashboard?${qs.toString()}`);
}

// Reproceso (Fase 5): inicia en background, devuelve Id. El frontend hace
// polling sobre listar para ver cuando termina y mostrar las novedades.
export function conciliacionReprocesoIniciarJWT(fechaDesde, fechaHasta) {
    return jwtFetch('conciliacion-externa/reproceso/iniciar', {
        method: 'POST',
        body: JSON.stringify({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta }),
    });
}
export function conciliacionReprocesoListarJWT(limit) {
    const qs = limit ? `?limit=${limit}` : '';
    return jwtFetch(`conciliacion-externa/reproceso/listar${qs}`);
}
export function conciliacionReprocesoObtenerJWT(id) {
    return jwtFetch(`conciliacion-externa/reproceso/${id}`);
}

// ─── INACTIVIDAD (Mi Actividad — tablero personal del abogado) ───
export function inactividadStatsJWT({ fechaAsignacionDesde, soloEnTramite } = {}) {
    const qs = new URLSearchParams();
    if (fechaAsignacionDesde) qs.append('fechaAsignacionDesde', fechaAsignacionDesde);
    if (soloEnTramite !== undefined) qs.append('soloEnTramite', soloEnTramite ? 'true' : 'false');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return jwtFetch(`inactividad/stats${suffix}`);
}
export function inactividadListarJWT(params = {}) {
    const {
        fechaCorte, fechaAsignacionDesde, fechaAsignacionHasta, modulos, tiposProceso,
        busqueda, soloEnTramite, page, pageSize,
        // Filtros avanzados (replicados de busqueda-avanzada de expedientes)
        autos, caratula, certificado, cartaPoder, demandado,
        estadosProceso, juzgado, etapasProcesales, objetoReclamo, reparticionOrigen,
        montoDesde, montoHasta, fechaInicioDesde, fechaInicioHasta,
        // Tab personal (filtro del abogado): número, 'sin-asignar' o undefined
        idTab,
    } = params;
    const qs = new URLSearchParams();
    if (fechaCorte) qs.append('fechaCorte', fechaCorte);
    if (fechaAsignacionDesde) qs.append('fechaAsignacionDesde', fechaAsignacionDesde);
    if (fechaAsignacionHasta) qs.append('fechaAsignacionHasta', fechaAsignacionHasta);
    if (modulos && modulos.length) qs.append('modulos', modulos.join(','));
    if (tiposProceso && tiposProceso.length) qs.append('tiposProceso', tiposProceso.join(','));
    if (busqueda) qs.append('busqueda', busqueda);
    if (soloEnTramite !== undefined) qs.append('soloEnTramite', soloEnTramite ? 'true' : 'false');
    if (autos) qs.append('autos', autos);
    if (caratula) qs.append('caratula', caratula);
    if (certificado) qs.append('certificado', certificado);
    if (cartaPoder) qs.append('cartaPoder', cartaPoder);
    if (demandado) qs.append('demandado', demandado);
    if (estadosProceso && estadosProceso.length) qs.append('estadosProceso', estadosProceso.join(','));
    if (juzgado && juzgado.length) qs.append('juzgado', juzgado.join(','));
    if (etapasProcesales && etapasProcesales.length) qs.append('etapasProcesales', etapasProcesales.join(','));
    if (objetoReclamo && objetoReclamo.length) qs.append('objetoReclamo', objetoReclamo.join(','));
    if (reparticionOrigen && reparticionOrigen.length) qs.append('reparticionOrigen', reparticionOrigen.join(','));
    if (montoDesde != null && montoDesde !== '') qs.append('montoDesde', String(montoDesde));
    if (montoHasta != null && montoHasta !== '') qs.append('montoHasta', String(montoHasta));
    if (fechaInicioDesde) qs.append('fechaInicioDesde', fechaInicioDesde);
    if (fechaInicioHasta) qs.append('fechaInicioHasta', fechaInicioHasta);
    if (idTab !== undefined && idTab !== null && idTab !== '') qs.append('idTab', String(idTab));
    if (page) qs.append('page', String(page));
    if (pageSize) qs.append('pageSize', String(pageSize));
    return jwtFetch(`inactividad/mis-expedientes?${qs.toString()}`);
}

// Tabs personales del abogado en "Mi Actividad".
// Acepta filtros globales (fechaAsignacionDesde, soloEnTramite) para que los
// counts de cada tab coincidan con el listado.
export function misTabsListarJWT({ fechaAsignacionDesde, soloEnTramite } = {}) {
    const qs = new URLSearchParams();
    if (fechaAsignacionDesde) qs.append('fechaAsignacionDesde', fechaAsignacionDesde);
    if (soloEnTramite !== undefined) qs.append('soloEnTramite', soloEnTramite ? 'true' : 'false');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return jwtFetch(`inactividad/tabs${suffix}`);
}
export function misTabsCrearJWT(nombre, color) {
    return jwtFetch('inactividad/tabs', { method: 'POST', body: JSON.stringify({ nombre, color }) });
}
export function misTabsActualizarJWT(id, datos) {
    return jwtFetch(`inactividad/tabs/${id}`, { method: 'PUT', body: JSON.stringify(datos) });
}
export function misTabsEliminarJWT(id) {
    return jwtFetch(`inactividad/tabs/${id}`, { method: 'DELETE' });
}
export function misTabsAsignarJWT(idTab, idExpediente) {
    return jwtFetch(`inactividad/tabs/${idTab}/asignar`, { method: 'POST', body: JSON.stringify({ idExpediente }) });
}
export function misTabsDesasignarJWT(idExpediente) {
    return jwtFetch('inactividad/tabs/desasignar', { method: 'POST', body: JSON.stringify({ idExpediente }) });
}
// Asigna múltiples expedientes al mismo tab de una. idTab=null desasigna a todos.
export function misTabsAsignarMasivoJWT(idsExpedientes, idTab) {
    return jwtFetch('inactividad/tabs/asignar-masivo', {
        method: 'POST',
        body: JSON.stringify({ idsExpedientes, idTab }),
    });
}

// ─── EVENTOS (audiencias, vencimientos, reuniones del expediente) ───
export function eventosListarJWT(idExp) {
    return jwtFetch(`eventos/expediente/${idExp}`);
}
export function eventosCrearJWT(payload) {
    return jwtFetch('eventos', { method: 'POST', body: JSON.stringify(payload) });
}
export function eventosActualizarJWT(id, payload) {
    return jwtFetch(`eventos/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}
export function eventosEliminarJWT(id) {
    return jwtFetch(`eventos/${id}`, { method: 'DELETE' });
}
export function eventosProximosAbogadoJWT() {
    return jwtFetch('eventos/proximos-abogado');
}
// Personas adicionales a notificar (#11). Solo usuarios con permiso
// "SECRETARIA GENERAL Y DAP" pueden setear desde la UI.
export function eventosGetCompartidosJWT(idEvento) {
    return jwtFetch(`eventos/${idEvento}/compartidos`);
}
export function eventosSetCompartidosJWT(idEvento, idsPersonal) {
    return jwtFetch(`eventos/${idEvento}/compartidos`, {
        method: 'PUT',
        body: JSON.stringify({ idsPersonal }),
    });
}

// ─── BANDEJA DE SALIDA (cola de docs del abogado para subir al PJ) ───
export function bandejaSalidaListarJWT() {
    return jwtFetch('bandeja-salida/mi-bandeja');
}
export function bandejaSalidaAgregarJWT(payload) {
    return jwtFetch('bandeja-salida', { method: 'POST', body: JSON.stringify(payload) });
}
export function bandejaSalidaQuitarJWT(id) {
    return jwtFetch(`bandeja-salida/${id}`, { method: 'DELETE' });
}
export async function bandejaSalidaDescargarBlob(id) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    const res = await fetch(nodeApiPath(`bandeja-salida/${id}/descargar`), {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('No se pudo descargar');
    return await res.blob();
}
export async function bandejaSalidaDescargarZipBlob(idsExpediente) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    const qs = idsExpediente && idsExpediente.length
        ? `?idsExpediente=${idsExpediente.join(',')}`
        : '';
    const res = await fetch(nodeApiPath(`bandeja-salida/zip${qs}`), {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('No se pudo descargar ZIP');
    return await res.blob();
}
